import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPost } from '../utils/api';
import { devLog } from '../utils/logger';

interface Tournament {
  id: string;
  name: string;
}

interface StandingsRow {
  player: {
    id: string;
    name: string;
  };
  matchesPlayed: number;
  matchesWon: number;
  matchesLost: number;
  setsWon: number;
  setsLost: number;
  gamesWon: number;
  gamesLost: number;
  setsWonPercent: number;
  gamesWonPercent: number;
}

interface Match {
  id: string;
  matchCode?: string;
  stageShort?: string | null;
  stageMatchNumber?: number | null;
  roundName?: string | null;
  round?: { name?: string | null } | null;
  player1Id?: string;
  player2Id?: string;
  player1?: { id: string; name: string };
  player2?: { id: string; name: string };
  player3?: { id: string; name: string };
  player4?: { id: string; name: string };
  winnerId?: string;
  winner2Id?: string;
  score?: string;
  result?: string;
  status: string;
  team1Id?: string; // Для парных турниров: ID первой команды
  team2Id?: string; // Для парных турниров: ID второй команды
}

interface CacheStatusInfo {
  tournamentId: string;
  levelCode: string;
  resultsUpdatedAt: string | null;
  cacheUpdatedAt: string | null;
  cacheStatus: 'ready' | 'in_progress' | 'error' | 'stale' | 'missing';
  cacheSize: number | null;
  cacheIsSmall?: boolean;
  cacheLastReason: string | null;
}

interface MatchResult {
  sets: Array<{
    set: number;
    player1: number;
    player2: number;
    tiebreak?: number | null;
    superTiebreak?: boolean; // Флаг решающего тайбрейка
  }>;
  walkover?: boolean;
  retired?: {
    player: 'player1' | 'player2';
    reason?: string;
  };
  specialStatus?: {
    type: 'REFUSAL' | 'DISQUALIFICATION' | 'INJURY' | 'NO_SHOW' | 'SUSPENSION';
    player?: 'player1' | 'player2';
    reason?: string;
  };
  completedAt: string;
}

const AUTO_SPECIAL_STATUS_TYPES = new Set(['REFUSAL', 'DISQUALIFICATION', 'INJURY', 'NO_SHOW']);

const normalizeStartTime = (value: unknown): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return raw;

  const hours = match[1].padStart(2, '0');
  const minutes = match[2];
  return `${hours}:${minutes}`;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: 'Europe/Chisinau',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date).replace(/,\s*/g, ' • ');
};

const isCacheOlderThanResults = (cacheStatus: CacheStatusInfo | null): boolean => {
  if (!cacheStatus?.cacheUpdatedAt || !cacheStatus?.resultsUpdatedAt) return false;
  const cacheTs = new Date(cacheStatus.cacheUpdatedAt).getTime();
  const resultsTs = new Date(cacheStatus.resultsUpdatedAt).getTime();
  if (!Number.isFinite(cacheTs) || !Number.isFinite(resultsTs)) return false;
  return cacheTs < resultsTs;
};

const formatCourtLabel = (value: unknown): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (/^teren\b/i.test(raw)) {
    return raw;
  }

  return `teren ${raw}`;
};

const formatScheduleDateShort = (value: unknown): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

const getPlayoffMatchScheduleLabel = (match: any, hideScheduleMeta: boolean): string => {
  if (hideScheduleMeta) return '';
  if (!match) return '';

  const date = formatScheduleDateShort(match.reservation?.date || match.scheduledDate);
  const time = normalizeStartTime(match.reservation?.startTime || match.scheduledTime);
  const court = formatCourtLabel(match.reservation?.court?.name || match.court?.name);
  return [date, time, court].filter(Boolean).join(' • ');
};

const isTeam2WinnerInMatch = (match: Match): boolean => {
  // Для парных матчей winnerId может указывать на любого игрока команды.
  const team2PlayerIds = [match.player2Id, match.player4?.id].filter(Boolean) as string[];
  if (team2PlayerIds.length > 0 && match.winnerId) {
    return team2PlayerIds.includes(match.winnerId);
  }
  // Для одиночных матчей.
  return !!match.winnerId && match.winnerId === match.player2Id;
};

// Функция форматирования счета матча для турнирной таблицы (возвращает объект с двумя строками)
// Функция для зеркалирования счета (с точки зрения player2)
const mirrorScore = (setsScore: string, gamesScore: string): { setsScore: string; gamesScore: string } => {
  // Формат со спецстатусом в скобках: "2:0 (неявка)" -> "0:2 (неявка)"
  const bracketStatusMatch = setsScore.match(/^(\d+):(\d+)\s*\((.+)\)$/);
  if (bracketStatusMatch) {
    const [, left, right, status] = bracketStatusMatch;
    return {
      setsScore: `${right}:${left} (${status})`,
      gamesScore
    };
  }

  // Зеркалируем счет по сетам: "2:0" -> "0:2"
  let mirroredSetsScore = setsScore;
  const setsParts = setsScore.split(':');
  if (setsParts.length === 2) {
    // Проверяем, нет ли специального статуса (например, "1:1 / int")
    if (!setsScore.includes(' / ')) {
      mirroredSetsScore = `${setsParts[1]}:${setsParts[0]}`;
    } else {
      // Если есть специальный статус, зеркалируем только счет
      const [scorePart, statusPart] = setsScore.split(' / ');
      const scoreParts = scorePart.split(':');
      if (scoreParts.length === 2) {
        mirroredSetsScore = `${scoreParts[1]}:${scoreParts[0]} / ${statusPart}`;
      }
    }
  }
  
  // Зеркалируем счет по геймам: "7:6(2) 6:3" -> "6:7(2) 3:6"
  let mirroredGamesScore = gamesScore;
  if (gamesScore) {
    // Разбиваем на отдельные сеты
    const sets = gamesScore.split(' ');
    const mirroredSets = sets.map(set => {
      // Проверяем, есть ли тайбрейк в скобках (формат: "7:6(2)" или "6:7(2)")
      const tiebreakMatch = set.match(/^(\d+):(\d+)\((\d+)\)$/);
      if (tiebreakMatch) {
        // Сет с тайбрейком: "7:6(2)" -> "6:7(2)"
        // Счет в скобках - это счет проигравшего, он не меняется
        const [, p1, p2, tb] = tiebreakMatch;
        return `${p2}:${p1}(${tb})`;
      }
      // Обычный сет: "6:3" -> "3:6"
      const gameParts = set.split(':');
      if (gameParts.length === 2) {
        return `${gameParts[1]}:${gameParts[0]}`;
      }
      return set;
    });
    mirroredGamesScore = mirroredSets.join(' ');
  }
  
  return { setsScore: mirroredSetsScore, gamesScore: mirroredGamesScore };
};

const formatMatchScoreForTable = (match: Match, rowPlayerId?: string): { setsScore: string; gamesScore: string; isSpecial: boolean } => {
  // Если есть result (JSON), используем его для форматирования
  if (match.result) {
    try {
      const result: MatchResult = JSON.parse(match.result);
      
      // Специальные статусы - но сначала формируем счет, если есть сеты
      let scoreString = '';
      let gamesScoreString = '';
      
      if (result.sets && result.sets.length > 0) {
        // Проверяем, есть ли специальный статус (матч не завершен)
        const hasSpecialStatus = !!result.specialStatus;
        
        // Подсчитываем общий счет по сетам
        let player1Sets = 0;
        let player2Sets = 0;
        
        // Подсчитываем завершенные сеты
        result.sets.forEach((set, index) => {
          const isLastSet = index === result.sets.length - 1;
          const hasTiebreak = set.tiebreak !== null && set.tiebreak !== undefined;
          
          // Определяем, является ли это решающим тайбрейком
          // ТОЛЬКО по явному флагу - не определяем по счету геймов!
          const isSuperTiebreak = set.superTiebreak === true;
          
          // Определяем, завершен ли сет
          let isSetCompleted = false;
          
          if (isSuperTiebreak) {
            // Для решающего тайбрейка проверяем, завершен ли он
            if (hasTiebreak && set.tiebreak !== null) {
              // Если тайбрейк указан, проверяем по тайбрейку
              if (typeof set.tiebreak === 'object' && 'player1' in set.tiebreak && 'player2' in set.tiebreak) {
                const { player1: tb1, player2: tb2 } = set.tiebreak;
                // Решающий тайбрейк завершен, если один из игроков достиг 10 очков с разницей минимум 2
                const maxScore = Math.max(tb1, tb2);
                const minScore = Math.min(tb1, tb2);
                isSetCompleted = maxScore >= 10 && (maxScore - minScore) >= 2;
              } else if (typeof set.tiebreak === 'number') {
                // Старый формат - считаем завершенным, если есть тайбрейк и счет >= 10
                isSetCompleted = set.tiebreak >= 10;
              }
            } else {
              // Если тайбрейк не указан, но счет геймов > 7, проверяем по счету геймов
              // Решающий тайбрейк завершен, если один из игроков достиг 10 очков с разницей минимум 2
              const maxScore = Math.max(set.player1, set.player2);
              const minScore = Math.min(set.player1, set.player2);
              isSetCompleted = maxScore >= 10 && (maxScore - minScore) >= 2;
            }
          } else if (hasTiebreak) {
            // Обычный тайбрейк - считаем завершенным, если есть тайбрейк
            isSetCompleted = true;
          } else {
            // Обычный сет - завершен, если один из игроков выиграл (>= 6 с разницей >= 2, или 7)
            if (set.player1 > set.player2) {
              // player1 выиграл, если player1 >= 6 и разница >= 2, или player1 = 7
              isSetCompleted = (set.player1 >= 6 && (set.player1 - set.player2) >= 2) || set.player1 === 7;
            } else if (set.player2 > set.player1) {
              // player2 выиграл
              isSetCompleted = (set.player2 >= 6 && (set.player2 - set.player1) >= 2) || set.player2 === 7;
            }
          }
          
          // Если есть специальный статус, считаем только завершенные сеты
          // Если специального статуса нет, считаем все сеты (матч завершен)
          if (!hasSpecialStatus || isSetCompleted) {
            // Определяем победителя сета с учетом тайбрейка
            let setWinner: 'player1' | 'player2' | null = null;
            
            if (hasTiebreak && set.tiebreak !== null) {
              // Если есть тайбрейк, победитель определяется по тайбрейку
              if (typeof set.tiebreak === 'object' && 'player1' in set.tiebreak && 'player2' in set.tiebreak) {
                const { player1: tb1, player2: tb2 } = set.tiebreak;
                if (tb1 > tb2) {
                  setWinner = 'player1';
                } else if (tb2 > tb1) {
                  setWinner = 'player2';
                }
              } else if (typeof set.tiebreak === 'number') {
                // Старый формат: тайбрейк - это счет победителя
                // Для супер-тайбрейка (10:7) или обычного тайбрейка (7:6)
                if (isSuperTiebreak) {
                  // Для супер-тайбрейка победитель определяется по счету геймов (очков)
                  if (set.player1 > set.player2) {
                    setWinner = 'player1';
                  } else if (set.player2 > set.player1) {
                    setWinner = 'player2';
                  }
                } else {
                  // Для обычного тайбрейка при счете 7:6 или 6:7
                  if (set.player1 === 7 && set.player2 === 6) {
                    setWinner = 'player1';
                  } else if (set.player1 === 6 && set.player2 === 7) {
                    setWinner = 'player2';
                  }
                }
              }
            } else {
              // Если тайбрейка нет, победитель определяется по счету геймов
              if (set.player1 > set.player2) {
                setWinner = 'player1';
              } else if (set.player2 > set.player1) {
                setWinner = 'player2';
              }
            }
            
            if (setWinner === 'player1') {
              player1Sets++;
            } else if (setWinner === 'player2') {
              player2Sets++;
            }
          }
        });
        
        // Формируем строку счета каждого сета
        const setsScores = result.sets.map(set => {
          // Для супер-тайбрейка не добавляем скобки с тайбрейком
          if (set.superTiebreak) {
            return `${set.player1}:${set.player2}`;
          }
          
          if (set.tiebreak !== null && set.tiebreak !== undefined) {
            // Поддержка как нового формата (объект), так и старого (число)
            if (typeof set.tiebreak === 'object' && set.tiebreak.player1 !== undefined) {
              // Показываем только счет проигравшего (меньший счет)
              const loserScore = Math.min(set.tiebreak.player1, set.tiebreak.player2);
              return `${set.player1}:${set.player2}(${loserScore})`;
            } else if (typeof set.tiebreak === 'number') {
              // Старый формат (только счет победителя) - оставляем как есть
              return `${set.player1}:${set.player2}(${set.tiebreak})`;
            }
          }
          return `${set.player1}:${set.player2}`;
        }).join(' ');
        
        scoreString = `${player1Sets}:${player2Sets}`;
        gamesScoreString = setsScores;
      }
      
      // Специальные статусы
      if (result.specialStatus) {
        const statusLabels: Record<string, string> = {
          'REFUSAL': 'Отказ',
          'DISQUALIFICATION': 'Дисквалификация',
          'INJURY': 'Травма',
          'NO_SHOW': 'Неявка',
          'SUSPENSION': 'int'
        };
        const label = statusLabels[result.specialStatus.type] || result.specialStatus.type;
        // В таблице турнирных результатов не показываем причину (reason)
        const statusString = label;

        // Для служебных статусов в групповых матчах показываем фиксированный сетовый счет.
        if (AUTO_SPECIAL_STATUS_TYPES.has(result.specialStatus.type)) {
          const team2Winner = isTeam2WinnerInMatch(match);
          const setsToWin = 2;
          const shortStatus =
            result.specialStatus.type === 'NO_SHOW' ||
            result.specialStatus.type === 'INJURY' ||
            result.specialStatus.type === 'REFUSAL'
              ? 'W.O.'
              : statusString.toLowerCase();

          let player1Sets = 0;
          let player2Sets = 0;
          if (scoreString && /^\d+:\d+$/.test(scoreString)) {
            const [p1, p2] = scoreString.split(':').map(Number);
            player1Sets = Number.isFinite(p1) ? p1 : 0;
            player2Sets = Number.isFinite(p2) ? p2 : 0;
          }

          if (team2Winner) {
            player2Sets = Math.max(player2Sets, setsToWin);
          } else {
            player1Sets = Math.max(player1Sets, setsToWin);
          }

          const gamesWithStatus = gamesScoreString ? `${gamesScoreString} ${shortStatus}` : shortStatus;
          return { setsScore: `${player1Sets}:${player2Sets}`, gamesScore: gamesWithStatus, isSpecial: true };
        }
        
        // Если есть счет, добавляем его перед статусом
        if (scoreString) {
          return { setsScore: `${scoreString} / ${statusString}`, gamesScore: gamesScoreString, isSpecial: true };
        }
        return { setsScore: statusString, gamesScore: '', isSpecial: true };
      }
      
      if (result.walkover) {
        return { setsScore: scoreString || '2:0', gamesScore: gamesScoreString || 'W.O.', isSpecial: true };
      }
      
      if (result.retired) {
        const retiredScore = result.retired.player === 'player1' ? '2:0' : '0:2';
        const reason = result.retired.reason ? `Retired: ${result.retired.reason}` : 'Retired';
        return { setsScore: scoreString || retiredScore, gamesScore: gamesScoreString || reason, isSpecial: true };
      }
      
      // Если есть счет, возвращаем его
      if (scoreString) {
        return { setsScore: scoreString, gamesScore: gamesScoreString, isSpecial: false };
      }
    } catch (e) {
      console.error('Ошибка парсинга результата:', e);
    }
  }
  
  // Fallback на старый формат score, если result отсутствует
  const oldScore = match.score || '';
  // Пытаемся распарсить старый формат "1:2 (5:7 7:5 6:10)" или "5-7, 7-5, 6-10"
  if (oldScore.includes('(') && oldScore.includes(')')) {
    const matchParts = oldScore.match(/^(\d+:\d+)\s*\((.+)\)/);
    if (matchParts) {
      return { setsScore: matchParts[1], gamesScore: matchParts[2], isSpecial: false };
    }
  }
  // Если формат не распознан, возвращаем как есть
  return { setsScore: oldScore, gamesScore: '', isSpecial: false };
};

// Функция форматирования счета матча (для обратной совместимости)
const formatMatchScore = (match: Match): string => {
  const formatted = formatMatchScoreForTable(match);
  if (formatted.isSpecial || !formatted.gamesScore) {
    return formatted.setsScore;
  }
  return `${formatted.setsScore} (${formatted.gamesScore})`;
};

interface MatchProposal {
  id: string;
  player1Id: string;
  player2Id: string;
  availability?: {
    date: string;
    startTime: string;
    endTime: string;
    publicProposalStartTime?: string | null;
    publicProposalEndTime?: string | null;
    court?: {
      name: string;
      club?: {
        name: string;
      };
    };
  };
}

interface GroupStandings {
  group: {
    id: string;
    name: string;
    order: number | null;
  };
  standings: StandingsRow[];
  matches?: Match[];
  matchProposals?: MatchProposal[];
  drawPositions?: Array<{ playerId: string; position: number }>;
  error?: string;
}

interface TournamentLevel {
  level: string;
  playerCount: number;
}

interface BracketTemplate {
  id: string;
  name: string;
  format: string;
  playerCount: number;
  structure: string;
  currentStage?: number;
}

interface PlayoffStage {
  qualifiedFromGroups: number;
  playerCount: number;
  directFinal?: boolean;
  thirdPlaceMatch?: boolean;
  rounds: Array<{
    round: number;
    name: string;
    matches: number;
  }>;
}

interface PlayoffPlayer {
  id: string;
  name: string;
  groupName: string;
  position: number;
  playoffSlot?: number;
}

interface TournamentStandingsProps {
  exportTournamentId?: string;
  exportLevel?: string;
}

const TournamentStandings: React.FC<TournamentStandingsProps> = ({ 
  exportTournamentId, 
  exportLevel 
}) => {
  // Получаем параметры из URL для режима экспорта (если не переданы через props)
  const urlParams = useParams<{ tournamentId?: string; level?: string }>();
  const isExportMode = window.location.pathname.startsWith('/standings-export') || 
                       (exportTournamentId !== undefined && exportLevel !== undefined);
  
  // Логируем режим экспорта для отладки
  useEffect(() => {
    if (isExportMode) {
      devLog('[EXPORT] Режим экспорта активирован, pathname:', window.location.pathname, 'props:', { exportTournamentId, exportLevel });
    }
  }, [isExportMode, exportTournamentId, exportLevel]);
  
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [levels, setLevels] = useState<TournamentLevel[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const logosLoadedRef = useRef<Set<string>>(new Set()); // Отслеживаем, для каких турниров logos уже загружены
  const [standingsByGroup, setStandingsByGroup] = useState<GroupStandings[]>([]);
  const [bracketTemplate, setBracketTemplate] = useState<BracketTemplate | null>(null);
  const [playoffStage, setPlayoffStage] = useState<PlayoffStage | null>(null);
  const [qualifiedFromGroupsFromAPI, setQualifiedFromGroupsFromAPI] = useState<number>(0); // qualifiedFromGroups из ответа API (для режима экспорта)
  const [playoffPlayers, setPlayoffPlayers] = useState<PlayoffPlayer[]>([]);
  const [playoffDrawEntries, setPlayoffDrawEntries] = useState<any[]>([]); // Записи жеребьёвки для плей-офф
  const [playoffMatches, setPlayoffMatches] = useState<Match[]>([]); // Матчи плей-оффа
  const [maxStage, setMaxStage] = useState<number>(1); // Максимальный выводимый этап
  const [hideScheduleMetaForLevel, setHideScheduleMetaForLevel] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [cacheRefreshLoading, setCacheRefreshLoading] = useState(false);
  const [cacheRefreshFeedback, setCacheRefreshFeedback] = useState<string>('');
  const [cacheStatusInfo, setCacheStatusInfo] = useState<CacheStatusInfo | null>(null);
  const [se4MeasuredSemifinalLocalCenters, setSe4MeasuredSemifinalLocalCenters] = useState<[number | null, number | null]>([null, null]);
  const [se4MeasuredFinalLocalCenter, setSe4MeasuredFinalLocalCenter] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(true); // Флаг для отслеживания восстановления
  const se4SemifinalCardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const se4FinalCardRef = useRef<HTMLDivElement | null>(null);
  const tournamentStatusMeta: Record<string, { order: number; label: string }> = {
    ACTIVE: { order: 0, label: 'Активные' },
    PLANNED: { order: 1, label: 'Запланированные' },
    COMPLETED: { order: 2, label: 'Завершенные' },
    CANCELLED: { order: 3, label: 'Отмененные' }
  };

  const tournamentsGrouped = useMemo(() => {
    const toDateTs = (value?: string): number => {
      if (!value) return Number.NEGATIVE_INFINITY;
      const parsed = new Date(value).getTime();
      return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
    };

    const groups = new Map<string, { label: string; order: number; items: Tournament[] }>();
    for (const tournament of tournaments) {
      const statusKey = String(tournament.status || '').trim().toUpperCase();
      const statusInfo = tournamentStatusMeta[statusKey] || { order: 99, label: 'Прочие' };
      if (!groups.has(statusInfo.label)) {
        groups.set(statusInfo.label, { label: statusInfo.label, order: statusInfo.order, items: [] });
      }
      groups.get(statusInfo.label)!.items.push(tournament);
    }

    const sortedGroups = Array.from(groups.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'ru'));
    for (const group of sortedGroups) {
      group.items.sort((a, b) => {
        const aTs = Math.max(toDateTs(a.endDate), toDateTs(a.startDate));
        const bTs = Math.max(toDateTs(b.endDate), toDateTs(b.startDate));
        if (aTs !== bTs) return bTs - aTs;
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru');
      });
    }
    return sortedGroups;
  }, [tournaments]);

  const shouldShowPlayoffSeedMeta = (player?: PlayoffPlayer | null): boolean => {
    if (!player) return false;
    if ((playoffStage?.qualifiedFromGroups || 0) <= 0) return false;
    const hasGroup = Boolean(player.groupName && player.groupName.trim().length > 0);
    const hasPlace = Number.isFinite(Number(player.position)) && Number(player.position) > 0;
    return hasGroup || hasPlace;
  };

  const getCompactGroupLabel = (groupName: string): string => {
    const normalized = String(groupName || '').trim();
    if (!normalized) return '';
    return normalized.split(/\s+/)[0] || '';
  };

  const getPlayoffRoundDisplayName = (round: { name: string; matches: number }): string => {
    if (round.matches === 16) {
      return 'R32';
    }
    // Для SE-16 в первом раунде используем корректный румынский заголовок.
    if (round.matches === 8) {
      return 'Optimi de finală';
    }
    return round.name;
  };

  // Восстанавливаем сохраненные фильтры при загрузке
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        devLog('[EXPORT] loadInitialData: начало, isExportMode:', isExportMode, 'pathname:', window.location.pathname);
        setLoading(true);
        setIsRestoring(true);
        
        // В режиме экспорта используем параметры из props или из URL через useParams
        if (isExportMode) {
          devLog('[EXPORT] loadInitialData: режим экспорта, параметры:', { exportTournamentId, exportLevel, urlParams });
          const urlTournamentId = exportTournamentId || urlParams.tournamentId;
          const urlLevel = exportLevel || (urlParams.level ? decodeURIComponent(urlParams.level) : null);
          
          devLog('[EXPORT] loadInitialData: извлеченные параметры:', { urlTournamentId, urlLevel });
          
          if (urlTournamentId && urlLevel) {
            // Загружаем только нужный турнир через публичный API
            try {
              devLog('[EXPORT] loadInitialData: запрос к публичному API:', `/api/tournaments/${urlTournamentId}/public`);
              const response = await fetch(`/api/tournaments/${urlTournamentId}/public`);
              devLog('[EXPORT] loadInitialData: ответ от API:', response.status, response.statusText);
              if (!response.ok) {
                throw new Error(`Ошибка загрузки турнира: ${response.status} ${response.statusText}`);
              }
              const tournament = await response.json();
              devLog('[EXPORT] loadInitialData: турнир загружен:', tournament?.id, tournament?.name);
              setTournaments([tournament]);
              
              // В режиме экспорта сразу загружаем уровни из memberships
              const levelMap = new Map<string, number>();
              if (tournament.memberships) {
                tournament.memberships.forEach((m: any) => {
                  if (m.level) {
                    levelMap.set(m.level, (levelMap.get(m.level) || 0) + 1);
                  }
                });
              }
              const levelsList = Array.from(levelMap.entries())
                .map(([level, count]) => ({ level, playerCount: count }))
                .sort((a, b) => a.level.localeCompare(b.level));
              setLevels(levelsList);
              
              // Устанавливаем турнир и уровень после загрузки уровней
              setSelectedTournament(tournament);
              setSelectedLevel(urlLevel);
              
              devLog('[EXPORT] Турнир загружен, уровни установлены:', levelsList.length);
            } catch (error) {
              console.error('Ошибка загрузки турнира для экспорта:', error);
            }
          }
        } else {
          // Обычный режим - загружаем все турниры
          devLog('[EXPORT] loadInitialData: обычный режим, загрузка турниров через apiGet');
          const data = await apiGet<{ tournaments: Tournament[] } | Tournament[]>('/tournaments?limit=1000');
          const tournamentsList = Array.isArray(data) ? data : (Array.isArray(data.tournaments) ? data.tournaments : []);
          setTournaments(tournamentsList);
          
          const savedTournamentId = localStorage.getItem('tournamentStandings_tournamentId');
          const savedLevel = localStorage.getItem('tournamentStandings_level');
          
          if (savedTournamentId) {
            // Используем загруженный список tournaments
            const tournament = tournamentsList.find(t => t.id === savedTournamentId);
            if (tournament) {
              setSelectedTournament(tournament);
              // Если есть сохраненный уровень, устанавливаем его сразу
              // Он будет проверен после загрузки уровней
              if (savedLevel) {
                setSelectedLevel(savedLevel);
              }
            }
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки турниров:', error);
        setTournaments([]);
      } finally {
        setLoading(false);
        // Даем время на восстановление, затем снимаем флаг
        setTimeout(() => setIsRestoring(false), 500);
      }
    };
    
    loadInitialData();
  }, []);

  // Сохраняем фильтры в localStorage при их изменении (только если не восстанавливаем)
  useEffect(() => {
    if (isRestoring) return; // Не сохраняем во время восстановления
    
    if (selectedTournament) {
      localStorage.setItem('tournamentStandings_tournamentId', selectedTournament.id);
    } else {
      localStorage.removeItem('tournamentStandings_tournamentId');
    }
  }, [selectedTournament, isRestoring]);

  useEffect(() => {
    if (isRestoring) return; // Не сохраняем во время восстановления
    
    if (selectedLevel) {
      localStorage.setItem('tournamentStandings_level', selectedLevel);
    } else {
      localStorage.removeItem('tournamentStandings_level');
    }
  }, [selectedLevel, isRestoring]);

  useEffect(() => {
    // В режиме экспорта не вызываем fetchLevels (уровни уже загружены)
    const isExportMode = window.location.pathname.startsWith('/standings-export');
    if (isExportMode) {
      return;
    }
    
    if (selectedTournament) {
      fetchLevels();
    } else {
      setLevels([]);
      // Не сбрасываем уровень, если восстанавливаем данные
      if (!isRestoring) {
        setSelectedLevel('');
    }
    }
  }, [selectedTournament, isRestoring]);

  useEffect(() => {
    if (selectedTournament && selectedLevel) {
      const isExportMode = window.location.pathname.startsWith('/standings-export');
      
      // В режиме экспорта загружаем standings и bracketTemplate (для maxStage и playoffStage)
      if (isExportMode) {
        fetchStandings();
        fetchBracketTemplate(); // Загружаем для определения maxStage и playoffStage
        fetchPlayoffDrawEntries();
        fetchPlayoffMatches();
      } else {
        fetchStandings();
        fetchBracketTemplate();
        fetchPlayoffDrawEntries();
        fetchPlayoffMatches();
      }
    }
  }, [selectedTournament, selectedLevel]);

  useEffect(() => {
    if (selectedTournament && selectedLevel) {
      fetchCacheStatus();
    } else {
      setCacheStatusInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTournament, selectedLevel]);

  useEffect(() => {
    if (playoffStage) {
      determinePlayoffPlayers();
    } else {
      setPlayoffPlayers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standingsByGroup, playoffStage, playoffDrawEntries, playoffMatches]);

  useEffect(() => {
    const isSe4 = playoffStage?.rounds?.[0]?.matches === 2;
    if (!isSe4) {
      if (se4MeasuredSemifinalLocalCenters[0] !== null || se4MeasuredSemifinalLocalCenters[1] !== null) {
        setSe4MeasuredSemifinalLocalCenters([null, null]);
      }
      if (se4MeasuredFinalLocalCenter !== null) setSe4MeasuredFinalLocalCenter(null);
      return;
    }

    const measureSe4Geometry = () => {
      const semifinalTopEl = se4SemifinalCardRefs.current[0];
      const semifinalBottomEl = se4SemifinalCardRefs.current[1];
      const finalCardEl = se4FinalCardRef.current;
      const nextTopCenter = semifinalTopEl ? semifinalTopEl.getBoundingClientRect().height / 2 : null;
      const nextBottomCenter = semifinalBottomEl ? semifinalBottomEl.getBoundingClientRect().height / 2 : null;
      const nextFinalCenter = finalCardEl ? finalCardEl.getBoundingClientRect().height / 2 : null;

      setSe4MeasuredSemifinalLocalCenters((prev) => {
        const [prevTop, prevBottom] = prev;
        const topChanged =
          (prevTop === null) !== (nextTopCenter === null) ||
          (prevTop !== null && nextTopCenter !== null && Math.abs(prevTop - nextTopCenter) > 0.5);
        const bottomChanged =
          (prevBottom === null) !== (nextBottomCenter === null) ||
          (prevBottom !== null && nextBottomCenter !== null && Math.abs(prevBottom - nextBottomCenter) > 0.5);
        return topChanged || bottomChanged ? [nextTopCenter, nextBottomCenter] : prev;
      });

      setSe4MeasuredFinalLocalCenter((prev) => {
        if (nextFinalCenter === null) return prev === null ? prev : null;
        if (prev === null) return nextFinalCenter;
        return Math.abs(prev - nextFinalCenter) > 0.5 ? nextFinalCenter : prev;
      });
    };

    const rafId = requestAnimationFrame(measureSe4Geometry);
    window.addEventListener('resize', measureSe4Geometry);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measureSe4Geometry);
    };
  }, [
    playoffStage,
    playoffMatches,
    playoffPlayers,
    standingsByGroup,
    se4MeasuredSemifinalLocalCenters,
    se4MeasuredFinalLocalCenter
  ]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const data = await apiGet<{ tournaments: Tournament[] } | Tournament[]>('/tournaments?limit=1000');
      if (Array.isArray(data)) {
        setTournaments(data);
      } else {
        setTournaments(Array.isArray(data.tournaments) ? data.tournaments : []);
      }
    } catch (error) {
      console.error('Ошибка загрузки турниров:', error);
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLevels = async () => {
    if (!selectedTournament) return;
    
    // В режиме экспорта не загружаем уровни (они уже загружены)
    const isExportMode = window.location.pathname.startsWith('/standings-export');
    if (isExportMode) {
      return;
    }
    
    try {
      const tournament = await apiGet<any>(`/tournaments/${selectedTournament.id}`);
      const memberships = tournament.memberships || [];
      const levelMap = new Map<string, number>();
      
      memberships.forEach((m: any) => {
        if (m.level) {
          levelMap.set(m.level, (levelMap.get(m.level) || 0) + 1);
        }
      });

      const levelsList: TournamentLevel[] = Array.from(levelMap.entries())
        .map(([level, count]) => ({ level, playerCount: count }))
        .sort((a, b) => a.level.localeCompare(b.level));

      setLevels(levelsList);
      
      // Синхронизируем logos в selectedTournament при любом изменении.
      const currentLogos = (selectedTournament as any).logos ?? null;
      const incomingLogos = tournament.logos ?? null;
      if (JSON.stringify(currentLogos) !== JSON.stringify(incomingLogos)) {
        setSelectedTournament({ ...selectedTournament, logos: tournament.logos } as any);
        logosLoadedRef.current.add(selectedTournament.id);
      }
      
      // Восстанавливаем сохраненный уровень после загрузки уровней
      // Проверяем, что уровень существует в списке и соответствует сохраненному
      const savedLevel = localStorage.getItem('tournamentStandings_level');
      if (savedLevel) {
        const levelExists = levelsList.some(l => l.level === savedLevel);
        if (levelExists) {
          // Устанавливаем уровень, даже если он уже установлен (для синхронизации)
          setSelectedLevel(savedLevel);
        } else if (isRestoring) {
          // Если уровень не найден при восстановлении, очищаем его
          setSelectedLevel('');
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки уровней:', error);
      setLevels([]);
    }
  };

  const handleRefresh = async () => {
    if (selectedTournament && selectedLevel) {
      setStandingsByGroup([]);
      setPlayoffDrawEntries([]);
      setPlayoffMatches([]);
      setPlayoffPlayers([]);
      await Promise.all([
        fetchStandings(),
        fetchBracketTemplate(),
        fetchPlayoffDrawEntries(),
        fetchPlayoffMatches()
      ]);
      await fetchCacheStatus();
    } else if (selectedTournament) {
      await fetchLevels();
    } else {
      await fetchTournaments();
    }
  };

  const fetchCacheStatus = async () => {
    if (!selectedTournament || !selectedLevel) {
      setCacheStatusInfo(null);
      return;
    }

    try {
      const response = await apiGet<{ success: boolean; data: CacheStatusInfo }>('/standings/cache/status', {
        tournamentId: selectedTournament.id,
        levelCode: selectedLevel
      });
      setCacheStatusInfo(response?.data || null);
    } catch (error) {
      console.error('Ошибка загрузки статуса кэша:', error);
      setCacheStatusInfo(null);
    }
  };

  const handleCacheRefresh = async () => {
    if (!selectedTournament || !selectedLevel) return;
    setCacheRefreshLoading(true);
    setCacheRefreshFeedback('');
    try {
      await apiPost('/standings/cache/refresh', {
        tournamentId: selectedTournament.id,
        levelCode: selectedLevel
      });
      setCacheRefreshFeedback('Кэш поставлен на обновление. Обновляю данные...');
      await Promise.all([handleRefresh(), fetchCacheStatus()]);
    } catch (error: any) {
      const message = String(error?.message || 'Не удалось обновить кэш');
      setCacheRefreshFeedback(`Ошибка обновления кэша: ${message}`);
    } finally {
      setCacheRefreshLoading(false);
    }
  };

  const fetchStandings = async () => {
    if (!selectedTournament || !selectedLevel) {
      devLog('[EXPORT] fetchStandings: пропущено, нет турнира или уровня');
      return;
    }
    
    const isExportMode = window.location.pathname.startsWith('/standings-export');
    devLog('[EXPORT] fetchStandings: начало, isExportMode:', isExportMode, 'tournamentId:', selectedTournament.id, 'level:', selectedLevel);
    
    try {
      setLoading(true);
      
      // Проверяем, находимся ли мы в режиме экспорта (публичная страница)
      
      // Используем публичный API endpoint в режиме экспорта
      const endpoint = isExportMode
        ? `/standings/tournament/${selectedTournament.id}/level/${selectedLevel}/public`
        : `/standings/tournament/${selectedTournament.id}/level/${selectedLevel}`;
      
      devLog('[EXPORT] fetchStandings: запрос к', endpoint);
      
      let data: GroupStandings[] | { standingsByGroup: GroupStandings[]; qualifiedFromGroups?: number };
      if (isExportMode) {
        // В режиме экспорта используем fetch напрямую (без токена)
        const response = await fetch(`/api${endpoint}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        data = await response.json();
      } else {
        data = await apiGet<GroupStandings[]>(endpoint);
      }
      
      // Проверяем, является ли ответ объектом с standingsByGroup и qualifiedFromGroups
      let groupsData: GroupStandings[];
      if (data && typeof data === 'object' && 'standingsByGroup' in data) {
        groupsData = Array.isArray(data.standingsByGroup) ? data.standingsByGroup : [];
        // Сохраняем qualifiedFromGroups из ответа API для подсветки
        if ('qualifiedFromGroups' in data && typeof data.qualifiedFromGroups === 'number') {
          setQualifiedFromGroupsFromAPI(data.qualifiedFromGroups);
        }
      } else {
        groupsData = Array.isArray(data) ? data : [];
        setQualifiedFromGroupsFromAPI(0); // Сбрасываем, если ответ не содержит qualifiedFromGroups
      }
      
      devLog('[EXPORT] fetchStandings: получено групп:', groupsData.length);
      
      // В режиме экспорта match proposals уже включены в ответ от сервера
      if (isExportMode) {
        // Логируем match proposals для отладки
        groupsData.forEach((groupData: any) => {
          devLog(`[EXPORT] Группа ${groupData.group?.name}: matchProposals count:`, groupData.matchProposals?.length || 0);
          if (groupData.matchProposals && groupData.matchProposals.length > 0) {
            devLog(`[EXPORT] Match proposals для группы ${groupData.group?.name}:`, groupData.matchProposals.map((p: any) => ({
              id: p.id,
              player1Id: p.player1Id,
              player2Id: p.player2Id,
              hasAvailability: !!p.availability
            })));
          }
        });
        setStandingsByGroup(groupsData);
        devLog('[EXPORT] fetchStandings: завершено успешно (экспорт), групп:', groupsData.length);
      } else {
        // Загружаем match proposals для каждой группы
        const groupsWithProposals = await Promise.all(
          groupsData.map(async (groupData) => {
            try {
              const proposals = await apiGet<MatchProposal[]>(
                `/match-proposals/tournament/${selectedTournament.id}/group/${groupData.group.id}`
              );
              return {
                ...groupData,
                matchProposals: Array.isArray(proposals) ? proposals : []
              };
            } catch (error) {
              console.error(`Ошибка загрузки match proposals для группы ${groupData.group.id}:`, error);
              return {
                ...groupData,
                matchProposals: []
              };
            }
          })
        );
        
        setStandingsByGroup(groupsWithProposals);
        devLog('[EXPORT] fetchStandings: завершено успешно (обычный режим), групп:', groupsWithProposals.length);
      }
    } catch (error) {
      console.error('[EXPORT] Ошибка загрузки таблиц:', error);
      setStandingsByGroup([]);
    } finally {
      setLoading(false);
      devLog('[EXPORT] fetchStandings: loading установлен в false');
    }
  };

  const fetchPlayoffDrawEntries = async () => {
    if (!selectedTournament || !selectedLevel) return;
    
    const isExportMode = window.location.pathname.startsWith('/standings-export');
    
    try {
      let drawEntries: any[] = [];
      
      if (isExportMode) {
        // В режиме экспорта используем публичный endpoint
        const response = await fetch(`/api/draws/tournament/${selectedTournament.id}/level/${selectedLevel}/public?t=${Date.now()}`, {
          cache: 'no-store'
        });
        if (response.ok) {
          drawEntries = await response.json();
        }
      } else {
        drawEntries = await apiGet<any[]>(
          `/draws/tournament/${selectedTournament.id}/level/${selectedLevel}`
        );
      }
      
      // Фильтруем только записи для плей-офф (position начинается с "playoff-")
      const playoffEntries = (drawEntries || []).filter((entry: any) => 
        entry.position && entry.position.startsWith('playoff-')
      );
      
      setPlayoffDrawEntries(playoffEntries);
    } catch (error) {
      console.error('Ошибка загрузки жеребьёвки плей-офф:', error);
      setPlayoffDrawEntries([]);
    }
  };

  const fetchPlayoffMatches = async () => {
    if (!selectedTournament || !selectedLevel) return;

    try {
      let data: { matches: Match[]; groups: any[] } = { matches: [], groups: [] };

      // Для стабильного рендера сетки используем единый источник данных
      // (публичный endpoint уже содержит корректную дедупликацию Playoff-N).
      const response = await fetch(`/api/matches/tournament/${selectedTournament.id}/level/${selectedLevel}/public?t=${Date.now()}`, {
        cache: 'no-store'
      });
      if (response.ok) {
        data = await response.json();
      } else {
        // Fallback для локальных случаев/авторизации.
        data = await apiGet<{ matches: Match[]; groups: any[] }>(
          `/matches/tournament/${selectedTournament.id}/level/${selectedLevel}`
        );
      }
      
      // Фильтруем только матчи плей-оффа (без groupId или с matchCode, начинающимся с "Playoff")
      const playoff = (data.matches || []).filter((match: Match) => 
        !match.groupId || (match.matchCode && match.matchCode.startsWith('Playoff'))
      );
      
      setPlayoffMatches(playoff);
    } catch (error) {
      console.error('Ошибка загрузки матчей плей-оффа:', error);
      setPlayoffMatches([]);
    }
  };

  const fetchBracketTemplate = async () => {
    if (!selectedTournament || !selectedLevel) return;
    
    // В режиме экспорта тоже загружаем bracketTemplate для получения qualifiedFromGroups (для подсветки)
    try {
      // В режиме экспорта используем публичный API
      const isExportMode = window.location.pathname.startsWith('/standings-export');
      const tournament = isExportMode
        ? await fetch(`/api/tournaments/${selectedTournament.id}/public`).then(r => r.json())
        : await apiGet<any>(`/tournaments/${selectedTournament.id}`);
      
      // Синхронизируем logos в selectedTournament при любом изменении.
      const currentLogos = (selectedTournament as any).logos ?? null;
      const incomingLogos = tournament.logos ?? null;
      if (JSON.stringify(currentLogos) !== JSON.stringify(incomingLogos)) {
        setSelectedTournament({ ...selectedTournament, logos: tournament.logos } as any);
        logosLoadedRef.current.add(selectedTournament.id);
      }
      
      let levelBrackets: Record<string, any> = {};
      
      if (tournament.levelBrackets) {
        try {
          levelBrackets = typeof tournament.levelBrackets === 'string'
            ? JSON.parse(tournament.levelBrackets)
            : tournament.levelBrackets;
        } catch (e) {
          console.error('Ошибка парсинга levelBrackets:', e);
        }
      }

      const levelData = levelBrackets[selectedLevel];
      if (!levelData) {
        setBracketTemplate(null);
        setPlayoffStage(null);
        setHideScheduleMetaForLevel(false);
        return;
      }

      // Поддерживаем старый формат (просто templateId) и новый формат (объект)
      const templateId = typeof levelData === 'string' ? levelData : levelData.templateId;
      if (!templateId) {
        setBracketTemplate(null);
        setPlayoffStage(null);
        setMaxStage(1);
        setHideScheduleMetaForLevel(false);
        return;
      }

      const hideScheduleMeta =
        typeof levelData === 'object' && levelData !== null
          ? levelData.hideScheduleMeta === true
          : false;
      setHideScheduleMetaForLevel(hideScheduleMeta);

      // Получаем maxStage из настроек уровня
      const levelMaxStage = typeof levelData === 'object' && levelData !== null 
        ? (levelData.maxStage || 1) 
        : 1;
      setMaxStage(levelMaxStage);

      let template: BracketTemplate;
      if (isExportMode) {
        // В режиме экспорта используем публичный endpoint
        const response = await fetch(`/api/bracket-templates/${templateId}/public`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        template = await response.json();
      } else {
        template = await apiGet<BracketTemplate>(`/bracket-templates/${templateId}`);
      }
      setBracketTemplate(template);

      // Парсим структуру для определения второго этапа (плей-офф)
      try {
        const structure = typeof template.structure === 'string'
          ? JSON.parse(template.structure)
          : template.structure;

        devLog('Template format:', template.format);
        devLog('Structure:', structure);

        if (template.format === 'MIXED' && structure.stages) {
          // Определяем номер этапа плей-офф (второй этап в Mixed формате)
          const roundRobinStageIndex = structure.stages.findIndex((s: any) => s.type === 'ROUND_ROBIN');
          const playoffStageIndex = structure.stages.findIndex((s: any) => s.type === 'SINGLE_ELIMINATION');
          const playoffStageNumber = playoffStageIndex >= 0 ? playoffStageIndex + 1 : 2;
          
          const playoffStageData = structure.stages.find((s: any) => s.type === 'SINGLE_ELIMINATION');
          
          // Если maxStage меньше номера этапа плей-офф, не загружаем раунды, но сохраняем qualifiedFromGroups для подсветки
          if (levelMaxStage < playoffStageNumber) {
            if (playoffStageData?.qualifiedFromGroups) {
              setPlayoffStage({
                qualifiedFromGroups: playoffStageData.qualifiedFromGroups,
                playerCount: playoffStageData.playerCount || 0,
                directFinal: playoffStageData.directFinal || false,
                thirdPlaceMatch: playoffStageData.thirdPlaceMatch || false,
                rounds: [] // Раунды не загружаем, так как плей-офф еще не начался
              });
            } else {
              setPlayoffStage(null);
            }
            return;
          }
          devLog('Playoff stage data:', playoffStageData);
          if (playoffStageData) {
            // Получаем структуру раундов для Single Elimination
            let rounds: Array<{ round: number; name: string; matches: number }> = [];
            if (playoffStageData.templateId) {
              try {
                let playoffTemplate: BracketTemplate;
                if (isExportMode) {
                  // В режиме экспорта используем публичный endpoint
                  const response = await fetch(`/api/bracket-templates/${playoffStageData.templateId}/public`);
                  if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                  }
                  playoffTemplate = await response.json();
                } else {
                  playoffTemplate = await apiGet<BracketTemplate>(`/bracket-templates/${playoffStageData.templateId}`);
                }
                const playoffStructure = typeof playoffTemplate.structure === 'string'
                  ? JSON.parse(playoffTemplate.structure)
                  : playoffTemplate.structure;
                rounds = playoffStructure.rounds || [];
                devLog('Playoff template rounds:', rounds);
                
                // Нормализуем названия раундов на основе количества матчей
                rounds = rounds.map(round => {
                  let normalizedName = round.name;
                  if (round.matches === 1) {
                    normalizedName = 'Finala';
                  } else if (round.matches === 2) {
                    normalizedName = 'Semifinale';
                  } else if (round.matches === 4) {
                    normalizedName = 'Sferturi de finală';
                  } else if (round.matches === 8) {
                    normalizedName = '1/8 финала';
                  } else if (round.matches > 0) {
                    normalizedName = `1/${round.matches * 2} финала`;
                  }
                  return {
                    ...round,
                    name: normalizedName
                  };
                });
                devLog('Normalized rounds:', rounds);
              } catch (error) {
                console.error('Ошибка загрузки шаблона плей-офф:', error);
                // Продолжаем с генерацией раундов
              }
            }
            
            // Если раунды не были загружены из шаблона, генерируем их
            if (rounds.length === 0) {
              // Генерируем раунды на основе количества игроков
              const roundRobinStage = structure.stages.find((s: any) => s.type === 'ROUND_ROBIN');
              const groupsCount = roundRobinStage?.groups || 1;
              const qualifiedPerGroup = playoffStageData.qualifiedFromGroups || 0;
              const playerCount = playoffStageData.playerCount || (qualifiedPerGroup * groupsCount);
              
              devLog('Generating rounds for playerCount:', playerCount);
              
              const roundsCount = Math.ceil(Math.log2(playerCount));
              for (let i = roundsCount; i > 0; i--) {
                const matchesInRound = Math.pow(2, i - 1);
                let roundName = '';
                
                // Определяем имя раунда в зависимости от количества матчей (как в Draws.tsx)
                if (matchesInRound === 1) {
                  roundName = 'Finala';
                } else if (matchesInRound === 2) {
                  roundName = 'Semifinale';
                } else if (matchesInRound === 4) {
                  roundName = 'Sferturi de finală';
                } else if (matchesInRound === 8) {
                  roundName = '1/8 финала';
                } else {
                  roundName = `1/${matchesInRound * 2} финала`;
                }
                
                rounds.push({
                  round: i,
                  name: roundName,
                  matches: matchesInRound
                });
              }
            }

            devLog('Setting playoffStage with rounds:', rounds);
            
            setPlayoffStage({
              qualifiedFromGroups: playoffStageData.qualifiedFromGroups || 0,
              playerCount: playoffStageData.playerCount || 0,
              directFinal: playoffStageData.directFinal || false,
              thirdPlaceMatch: playoffStageData.thirdPlaceMatch || false,
              rounds
            });
          } else {
            setPlayoffStage(null);
          }
        } else if (template.format === 'SINGLE_ELIMINATION') {
          // Для чистого SINGLE_ELIMINATION показываем плей-офф сразу (без группового этапа)
          const rounds = Array.isArray(structure?.rounds) ? structure.rounds : [];
          const normalizedRounds = rounds.map((round: any) => {
            const matches = Number(round?.matches) || 0;
            let normalizedName = round?.name || '';
            if (matches === 1) {
              normalizedName = 'Finala';
            } else if (matches === 2) {
              normalizedName = 'Semifinale';
            } else if (matches === 4) {
              normalizedName = 'Sferturi de finală';
            } else if (matches > 0) {
              normalizedName = `1/${matches * 2} финала`;
            }
            return {
              round: Number(round?.round) || 0,
              matches,
              name: normalizedName
            };
          }).filter((r: any) => r.matches > 0);

          // Для одиночного плей-офф maxStage должен быть >= 2, иначе блок не рендерится
          setMaxStage(Math.max(levelMaxStage, 2));
          setPlayoffStage({
            qualifiedFromGroups: 0,
            playerCount: Number(structure?.playerCount) || 0,
            directFinal: false,
            thirdPlaceMatch: false,
            rounds: normalizedRounds
          });
        } else {
          setPlayoffStage(null);
        }
      } catch (e) {
        console.error('Ошибка парсинга структуры:', e);
        setPlayoffStage(null);
      }
    } catch (error) {
      console.error('Ошибка загрузки шаблона сетки:', error);
      setBracketTemplate(null);
      setPlayoffStage(null);
    }
  };

  const determinePlayoffPlayers = () => {
    if (!playoffStage) {
      setPlayoffPlayers([]);
      return;
    }

    const players: PlayoffPlayer[] = [];
    const parseSlot = (position: string | null | undefined): number => {
      if (!position) return NaN;
      const numeric = parseInt(String(position).replace('playoff-', '').replace(/[^\d]/g, ''), 10);
      return Number.isFinite(numeric) ? numeric : NaN;
    };

    const pushPlayersFromFirstRoundMatches = () => {
      const firstRoundMatches = playoffStage.rounds?.[0]?.matches || 0;
      const firstRound = [...playoffMatches]
        .filter((m) => m.matchCode?.startsWith('Playoff-'))
        .sort((a, b) => {
          const numA = parseInt((a.matchCode || '').replace('Playoff-', ''), 10) || 0;
          const numB = parseInt((b.matchCode || '').replace('Playoff-', ''), 10) || 0;
          return numA - numB;
        })
        .slice(0, firstRoundMatches);

      firstRound.forEach((match, idx) => {
        const slot1 = idx * 2 + 1;
        const slot2 = idx * 2 + 2;
        if (match.player1Id && match.player1?.name) {
          players.push({
            id: match.player1Id,
            name: match.player1.name,
            groupName: '',
            position: 0,
            playoffSlot: slot1
          });
        }
        if (match.player2Id && match.player2?.name) {
          players.push({
            id: match.player2Id,
            name: match.player2.name,
            groupName: '',
            position: 0,
            playoffSlot: slot2
          });
        }
      });
    };
    
    // Если есть сохраненная жеребьёвка для плей-офф, используем её
    if (playoffDrawEntries.length > 0) {
      const sortedEntries = [...playoffDrawEntries].sort((a, b) => {
        const posA = parseSlot(a.position);
        const posB = parseSlot(b.position);
        return (Number.isNaN(posA) ? 0 : posA) - (Number.isNaN(posB) ? 0 : posB);
      });

      sortedEntries.forEach((entry) => {
        const playoffSlot = parseSlot(entry.position);
        if (Number.isNaN(playoffSlot)) return;

        // Для SINGLE_ELIMINATION в draw_entries участники уже назначены напрямую.
        if (!entry.groupPlace) {
          if (entry.playerId && entry.player?.name) {
            players.push({
              id: entry.playerId,
              name: entry.player.name,
              groupName: '',
              position: 0,
              playoffSlot
            });
            return;
          }
          if (entry.player1Id && entry.player2Id && entry.player1?.name && entry.player2?.name) {
            players.push({
              id: `${entry.player1Id}:${entry.player2Id}`,
              name: `${entry.player1.name} / ${entry.player2.name}`,
              groupName: '',
              position: 0,
              playoffSlot
            });
          }
          return;
        }

        // Парсим groupPlace (например, "A-1" -> группа "A", место 1)
        const [groupLetter, placeStr] = entry.groupPlace.split('-');
        const place = parseInt(placeStr, 10);
        
        if (isNaN(place)) return;
        
        // Находим группу по имени (может быть "A", "4-A", "Группа A" и т.д.)
        let targetGroup: any = null;
        let targetStandings: any[] = [];
        
        for (const { group, standings } of standingsByGroup) {
          const groupName = group.name.toUpperCase();
          if (groupName.includes(groupLetter.toUpperCase()) || 
              groupName.endsWith(groupLetter.toUpperCase()) ||
              groupName === groupLetter.toUpperCase()) {
            targetGroup = group;
            targetStandings = standings;
            break;
          }
        }
        
        if (!targetGroup) {
          const groupIndex = groupLetter.charCodeAt(0) - 65; // A=0, B=1, ...
          const sortedGroups = [...standingsByGroup].sort((a, b) => {
            const orderA = a.group.order ?? 999;
            const orderB = b.group.order ?? 999;
            return orderA - orderB;
          });
          
          if (sortedGroups[groupIndex]) {
            targetGroup = sortedGroups[groupIndex].group;
            targetStandings = sortedGroups[groupIndex].standings;
          }
        }

        if (targetStandings && targetStandings[place - 1]) {
          const standing = targetStandings[place - 1];
          players.push({
            id: standing.player.id,
            name: standing.player.name,
            groupName: targetGroup?.name || groupLetter,
            position: place,
            playoffSlot
          });
        }
      });
    } else if (playoffStage.qualifiedFromGroups > 0) {
      const sortedGroups = [...standingsByGroup].sort((a, b) => {
        const orderA = a.group.order ?? 999;
        const orderB = b.group.order ?? 999;
        return orderA - orderB;
      });

      sortedGroups.forEach(({ group, standings }) => {
        const topPlayers = standings.slice(0, playoffStage.qualifiedFromGroups);
        topPlayers.forEach((standing, index) => {
          players.push({
            id: standing.player.id,
            name: standing.player.name,
            groupName: group.name,
            position: index + 1,
            playoffSlot: players.length + 1
          });
        });
      });
    } else {
      pushPlayersFromFirstRoundMatches();
    }

    // Если после всех источников ничего нет — фолбэк из первого раунда матчей.
    if (players.length === 0) {
      pushPlayersFromFirstRoundMatches();
    }

    setPlayoffPlayers(players);
  };

  // Проверяем, должен ли плей-офф отображаться выше группового этапа
  // Если maxStage >= 2, значит турнир на стадии плей-офф, и плей-офф должен быть выше
  const hasNextStageMatches = maxStage >= 2;

  // Функция для рендеринга плей-оффа (чтобы избежать дублирования кода)
  const renderPlayoffStage = () => {
    // Проверяем, должен ли отображаться плей-офф
    if (!selectedTournament || !selectedLevel || maxStage < 2) {
      return null;
    }
    
    // Если playoffStage не загружен, не отображаем плей-офф
    if (!playoffStage) {
      return null;
    }
    
    // Если раунды не загружены, не отображаем плей-офф
    if (!playoffStage.rounds || playoffStage.rounds.length === 0) {
      return null;
    }

    const bracketSideInsetPx = 32;
    const isPlayoff32 = playoffStage.rounds[0]?.matches === 16;
    const isPlayoff16 = playoffStage.rounds[0]?.matches === 8;
    // Экспорт должен повторять тот же визуальный layout, что и обычный браузерный режим.
    // Иначе ручные сдвиги этапов (под конкретные сетки) применяются только на странице,
    // а в экспортном рендере блоки могут "разъезжаться".
    const applyManualPlayoffTweaks = true;
    const disableCodeFallbackForPlayoff32 = isPlayoff32;
    const normalizeStageShort = (raw: unknown): string => {
      const value = String(raw || '').trim();
      if (!value) return '';
      const fraction = value.match(/\d+\s*\/\s*\d+/)?.[0]?.replace(/\s+/g, '');
      if (fraction) return fraction;

      const normalized = value.toLowerCase();
      if (normalized.includes('optimi') || normalized.includes('1/8')) return '1/8';
      if (normalized.includes('sfert') || normalized.includes('1/4') || normalized.includes('четверть')) return '1/4';
      if (normalized.includes('semi') || normalized.includes('1/2') || normalized.includes('полуфин')) return '1/2';
      if (normalized === 'finala' || normalized === 'final' || normalized === 'финал') return 'Финал';
      return value;
    };
    const getRoundStageShort = (round: { name: string; matches: number }): string => {
      const matches = Number(round.matches) || 0;
      // Для маппинга раундов в сетке опираемся на количество матчей,
      // а не на текст round.name, чтобы исключить ошибки вида
      // "1/16" для раунда с 8 матчами (Playoff-32 кейс).
      if (matches === 1) return 'Финал';
      if (matches === 2) return '1/2';
      if (matches === 4) return '1/4';
      if (matches === 8) return '1/8';
      if (matches === 16) return '1/16';
      if (matches === 32) return '1/32';

      const byName = normalizeStageShort(round.name);
      if (byName) return byName;
      return matches > 0 ? `1/${matches}` : '';
    };
    const getMatchStageShort = (match: Match | null | undefined): string => {
      if (!match) return '';
      return normalizeStageShort(match.stageShort || match.roundName || match.round?.name || '');
    };
    const getPlayoffMatchByStageAndSlot = (
      round: { name: string; matches: number },
      stageSlot: number,
      fallbackPlayoffNumber?: number,
      allowFallbackByCode: boolean = true
    ): Match | null => {
      const expectedStage = getRoundStageShort(round);
      const byStage = playoffMatches.find((m: Match) => {
        const matchStage = getMatchStageShort(m);
        const matchSlot = Number(m.stageMatchNumber || 0);
        return matchStage === expectedStage && matchSlot === stageSlot;
      });
      if (byStage) return byStage;

      const canUseFallbackByCode = allowFallbackByCode && !disableCodeFallbackForPlayoff32;
      if (canUseFallbackByCode && typeof fallbackPlayoffNumber === 'number' && fallbackPlayoffNumber > 0) {
        const fallbackCode = `Playoff-${fallbackPlayoffNumber}`;
        return playoffMatches.find((m: Match) => m.matchCode === fallbackCode) || null;
      }
      return null;
    };
    const playoffColumnsMinHeight =
      (playoffStage.rounds[0].matches * 90 + (playoffStage.rounds[0].matches - 1) * 80) + 35;
    
    return (
      <div className="bg-white rounded-lg shadow-md" style={{ padding: '24px', paddingBottom: playoffStage.directFinal && playoffStage.thirdPlaceMatch ? '60px' : '24px', marginTop: hasNextStageMatches ? 0 : 24, marginBottom: hasNextStageMatches ? 24 : 0 }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px', color: '#1f2937', textAlign: 'center' }}>FAZA ELIMINATORIE</h2>
        <div className="w-full">
          {playoffStage.directFinal ? (
            // Режим прямого финала: финал и чемпион горизонтально (в стиле старого формата)
            <div
              className="flex flex-wrap justify-center"
              style={{
                gap: '96px',
                width: '100%',
                paddingLeft: `${bracketSideInsetPx}px`,
                paddingRight: `${bracketSideInsetPx}px`,
                boxSizing: 'border-box'
              }}
            >
              {/* Финал */}
              {(() => {
                const matchHeight = 90;
                const cellWidth = '200px';
                
                // Находим матч финала (Playoff-1)
                const finalMatch = playoffMatches.find((m: Match) => m.matchCode === 'Playoff-1');
                
                // Определяем игроков финала
                let player1: PlayoffPlayer | null = null;
                let player2: PlayoffPlayer | null = null;
                
                if (finalMatch) {
                  if (selectedTournament?.format === 'DOUBLES') {
                    if (finalMatch.player1 && finalMatch.player3) {
                      const team1Name = `${finalMatch.player1.name} / ${finalMatch.player3.name}`;
                      player1 = playoffPlayers.find((p: PlayoffPlayer) => p.name === team1Name) || null;
                    }
                    if (finalMatch.player2 && finalMatch.player4) {
                      const team2Name = `${finalMatch.player2.name} / ${finalMatch.player4.name}`;
                      player2 = playoffPlayers.find((p: PlayoffPlayer) => p.name === team2Name) || null;
                    }
                  } else {
                    player1 = playoffPlayers.find((p: PlayoffPlayer) => p.id === finalMatch.player1Id) || null;
                    player2 = playoffPlayers.find((p: PlayoffPlayer) => p.id === finalMatch.player2Id) || null;
                  }
                } else {
                  // Если матч еще не создан, используем жеребьевку
                  if (playoffPlayers.length >= 2) {
                    player1 = playoffPlayers[0];
                    player2 = playoffPlayers[1];
                  }
                }
                
                // Извлекаем счет матча (геймы из последнего сета)
                let player1GamesScore: string | null = null;
                let player2GamesScore: string | null = null;
                let player1TiebreakScore: string | null = null;
                let player2TiebreakScore: string | null = null;
                
                if (finalMatch && finalMatch.result) {
                  try {
                    const result: MatchResult = JSON.parse(finalMatch.result);
                    if (result.sets && result.sets.length > 0) {
                      const lastSet = result.sets[result.sets.length - 1];
                      if (lastSet) {
                        player1GamesScore = lastSet.player1.toString();
                        player2GamesScore = lastSet.player2.toString();
                        
                        if (lastSet.tiebreak !== null && lastSet.tiebreak !== undefined) {
                          if (typeof lastSet.tiebreak === 'object' && 'player1' in lastSet.tiebreak && 'player2' in lastSet.tiebreak) {
                            player1TiebreakScore = lastSet.tiebreak.player1.toString();
                            player2TiebreakScore = lastSet.tiebreak.player2.toString();
                          } else if (typeof lastSet.tiebreak === 'number') {
                            if (lastSet.player1 > lastSet.player2) {
                              player1TiebreakScore = lastSet.tiebreak.toString();
                              player2TiebreakScore = Math.max(0, lastSet.tiebreak - 2).toString();
                            } else {
                              player1TiebreakScore = Math.max(0, lastSet.tiebreak - 2).toString();
                              player2TiebreakScore = lastSet.tiebreak.toString();
                            }
                          }
                        }
                      }
                    }
                  } catch (e) {
                    // Игнорируем ошибки парсинга
                  }
                }
                
                const finalScheduleLabel = getPlayoffMatchScheduleLabel(finalMatch, hideScheduleMetaForLevel);

                return (
                  <div className="flex flex-col items-center relative" style={{ order: 1 }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', textAlign: 'center', color: '#374151' }}>
                      Finala
                    </h3>
                    <div className="relative flex-shrink-0" style={{ minHeight: `${matchHeight}px`, width: cellWidth }}>
                      <div className="absolute left-0 flex items-center" style={{ top: '0px', width: '100%' }}>
                        <div className="relative flex items-center w-full pr-[28px]">
                          {finalScheduleLabel && (
                            <div
                              className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap"
                              style={{ top: '-18px', fontSize: '11px', color: '#6b7280', fontWeight: 500 }}
                            >
                              {finalScheduleLabel}
                            </div>
                          )}
                          <div className="border-2 border-gray-300 rounded-lg bg-white shadow-md relative z-10 flex-1" style={{ padding: '8px', minHeight: '90px', width: cellWidth }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div className={`rounded ${player1 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`} style={{ padding: '6px 8px' }}>
                                {player1 ? (
                                  <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                    {selectedTournament?.format === 'DOUBLES' && player1.name.includes(' / ') ? (
                                      <div className="font-medium text-gray-900">
                                        <div className="text-center" style={{ fontSize: '12px' }}>{player1.name.split(' / ')[0]}</div>
                                        <div className="text-center" style={{ fontSize: '12px', marginTop: '2px' }}>{player1.name.split(' / ')[1]}</div>
                                      </div>
                                    ) : (
                                      <div className="font-medium text-gray-900">
                                        <div className="text-center" style={{ fontSize: '12px' }}>{player1.name}</div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>—</div>
                                )}
                              </div>
                              <div className={`rounded ${player2 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`} style={{ padding: '6px 8px' }}>
                                {player2 ? (
                                  <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                    {selectedTournament?.format === 'DOUBLES' && player2.name.includes(' / ') ? (
                                      <div className="font-medium text-gray-900">
                                        <div className="text-center" style={{ fontSize: '12px' }}>{player2.name.split(' / ')[0]}</div>
                                        <div className="text-center" style={{ fontSize: '12px', marginTop: '2px' }}>{player2.name.split(' / ')[1]}</div>
                                      </div>
                                    ) : (
                                      <div className="font-medium text-gray-900">
                                        <div className="text-center" style={{ fontSize: '12px' }}>{player2.name}</div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>—</div>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Отображение счета справа от ячеек */}
                          {player1GamesScore !== null && player2GamesScore !== null && (
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center" style={{ right: '-20px', width: '52px', padding: '0 8px', boxSizing: 'border-box' }}>
                              <div className="flex items-start" style={{ gap: '2px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>{player1GamesScore}</span>
                                {player1TiebreakScore !== null && (
                                  <span style={{ fontSize: '9px', color: '#4b5563' }}>{player1TiebreakScore}</span>
                                )}
                              </div>
                              <div className="flex items-start" style={{ gap: '2px', marginTop: '4px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>{player2GamesScore}</span>
                                {player2TiebreakScore !== null && (
                                  <span style={{ fontSize: '9px', color: '#4b5563' }}>{player2TiebreakScore}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Горизонтальная линия к чемпиону (для SE-4 отключена) */}
                          {finalMatch && finalMatch.result && playoffStage.rounds[0]?.matches !== 2 && (
                            <div 
                              className="absolute h-0.5 bg-gray-300 z-0" 
                              style={{ 
                                left: '0px',
                                top: 'calc(50% - 1px)',
                                transform: 'translateY(-50%)',
                                width: '340px'
                              }}
                            ></div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Чемпион */}
              {(() => {
                const finalMatch = playoffMatches.find((m: Match) => m.matchCode === 'Playoff-1');
                let champion: PlayoffPlayer | null = null;
                
                if (finalMatch && finalMatch.winnerId && finalMatch.result) {
                  if (selectedTournament?.format === 'DOUBLES') {
                    if (finalMatch.winnerId && finalMatch.winner2Id && finalMatch.player1 && finalMatch.player3 && finalMatch.player2 && finalMatch.player4) {
                      const team1PlayerIds = [finalMatch.player1Id, finalMatch.player3Id].filter(Boolean);
                      const team2PlayerIds = [finalMatch.player2Id, finalMatch.player4Id].filter(Boolean);
                      
                      const team1Won = team1PlayerIds.includes(finalMatch.winnerId) && team1PlayerIds.includes(finalMatch.winner2Id);
                      const team2Won = team2PlayerIds.includes(finalMatch.winnerId) && team2PlayerIds.includes(finalMatch.winner2Id);
                      
                      if (team1Won) {
                        const team1Name = `${finalMatch.player1.name} / ${finalMatch.player3.name}`;
                        champion = playoffPlayers.find((p: PlayoffPlayer) => p.name === team1Name) || null;
                      } else if (team2Won) {
                        const team2Name = `${finalMatch.player2.name} / ${finalMatch.player4.name}`;
                        champion = playoffPlayers.find((p: PlayoffPlayer) => p.name === team2Name) || null;
                      }
                    }
                  } else {
                    champion = playoffPlayers.find((p: PlayoffPlayer) => p.id === finalMatch.winnerId) || null;
                  }
                }
                
                if (!champion) return null;
                
                const matchHeight = 90;
                const championContainerHeight = matchHeight;
                
                return (
                  <div className="flex flex-col items-center relative" style={{ order: 2, minHeight: `${matchHeight}px`, width: '200px', marginTop: '20px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', textAlign: 'center', color: '#374151' }}>
                      {selectedTournament?.format === 'DOUBLES' ? 'Campioni' : 'Campion'}
                    </h3>
                    <div className="relative flex-shrink-0" style={{ minHeight: `${matchHeight}px`, width: '200px' }}>
                      <div className="absolute left-0 flex items-center" style={{ top: '0px', width: '100%' }}>
                        <div className="border-2 border-yellow-400 rounded-lg bg-yellow-50 shadow-lg relative z-10 flex-1" style={{ padding: '12px', minHeight: '90px', width: '200px' }}>
                          <div
                            className="text-center"
                            style={
                              selectedTournament?.format === 'DOUBLES'
                                ? undefined
                                : { minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }
                            }
                          >
                            {selectedTournament?.format === 'DOUBLES' && champion.name.includes(' / ') ? (
                              <div className="font-bold text-gray-900">
                                <div className="text-center" style={{ fontSize: '16px' }}>{champion.name.split(' / ')[0]}</div>
                                <div className="text-center" style={{ fontSize: '16px', marginTop: '4px' }}>{champion.name.split(' / ')[1]}</div>
                              </div>
                            ) : (
                              <div className="font-bold text-gray-900" style={{ fontSize: '16px' }}>
                                {champion.name}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Матч за 3-е место - отображается ниже */}
              {playoffStage.thirdPlaceMatch && (() => {
                const matchHeight = 90;
                const cellWidth = '200px';
                
                const thirdPlaceMatch = playoffMatches.find((m: Match) => m.matchCode === 'Playoff-2');
                
                // Определяем игроков матча за 3-е место
                let player1: PlayoffPlayer | null = null;
                let player2: PlayoffPlayer | null = null;
                
                if (thirdPlaceMatch) {
                  if (selectedTournament?.format === 'DOUBLES') {
                    if (thirdPlaceMatch.player1 && thirdPlaceMatch.player3) {
                      const team1Name = `${thirdPlaceMatch.player1.name} / ${thirdPlaceMatch.player3.name}`;
                      player1 = playoffPlayers.find((p: PlayoffPlayer) => p.name === team1Name) || null;
                    }
                    if (thirdPlaceMatch.player2 && thirdPlaceMatch.player4) {
                      const team2Name = `${thirdPlaceMatch.player2.name} / ${thirdPlaceMatch.player4.name}`;
                      player2 = playoffPlayers.find((p: PlayoffPlayer) => p.name === team2Name) || null;
                    }
                  } else {
                    player1 = playoffPlayers.find((p: PlayoffPlayer) => p.id === thirdPlaceMatch.player1Id) || null;
                    player2 = playoffPlayers.find((p: PlayoffPlayer) => p.id === thirdPlaceMatch.player2Id) || null;
                  }
                } else {
                  // Если матч еще не создан, используем жеребьевку
                  if (playoffPlayers.length >= 4) {
                    player1 = playoffPlayers[2];
                    player2 = playoffPlayers[3];
                  }
                }
                
                // Извлекаем счет матча
                let player1GamesScore: string | null = null;
                let player2GamesScore: string | null = null;
                let player1TiebreakScore: string | null = null;
                let player2TiebreakScore: string | null = null;
                
                if (thirdPlaceMatch && thirdPlaceMatch.result) {
                  try {
                    const result: MatchResult = JSON.parse(thirdPlaceMatch.result);
                    if (result.sets && result.sets.length > 0) {
                      const lastSet = result.sets[result.sets.length - 1];
                      if (lastSet) {
                        player1GamesScore = lastSet.player1.toString();
                        player2GamesScore = lastSet.player2.toString();
                        
                        if (lastSet.tiebreak !== null && lastSet.tiebreak !== undefined) {
                          if (typeof lastSet.tiebreak === 'object' && 'player1' in lastSet.tiebreak && 'player2' in lastSet.tiebreak) {
                            player1TiebreakScore = lastSet.tiebreak.player1.toString();
                            player2TiebreakScore = lastSet.tiebreak.player2.toString();
                          } else if (typeof lastSet.tiebreak === 'number') {
                            if (lastSet.player1 > lastSet.player2) {
                              player1TiebreakScore = lastSet.tiebreak.toString();
                              player2TiebreakScore = Math.max(0, lastSet.tiebreak - 2).toString();
                            } else {
                              player1TiebreakScore = Math.max(0, lastSet.tiebreak - 2).toString();
                              player2TiebreakScore = lastSet.tiebreak.toString();
                            }
                          }
                        }
                      }
                    }
                  } catch (e) {
                    // Игнорируем ошибки парсинга
                  }
                }
                
                const thirdPlaceScheduleLabel = getPlayoffMatchScheduleLabel(thirdPlaceMatch, hideScheduleMetaForLevel);

                return (
                  <div className="flex flex-col items-center relative" style={{ order: 3, width: '100%', marginTop: '-60px', flexBasis: '100%' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', textAlign: 'center', color: '#374151' }}>
                      Meci pentru locul 3
                    </h3>
                    <div className="relative flex-shrink-0" style={{ minHeight: `${matchHeight}px`, width: cellWidth }}>
                      <div className="absolute left-0 flex items-center" style={{ top: '0px', width: '100%' }}>
                        <div className="relative flex items-center w-full pr-[28px]">
                          {thirdPlaceScheduleLabel && (
                            <div
                              className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap"
                              style={{ top: '-18px', fontSize: '11px', color: '#6b7280', fontWeight: 500 }}
                            >
                              {thirdPlaceScheduleLabel}
                            </div>
                          )}
                          <div className="border-2 border-gray-300 rounded-lg bg-white shadow-md relative z-10 flex-1" style={{ padding: '8px', minHeight: '90px', width: cellWidth }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div className={`rounded ${player1 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`} style={{ padding: '6px 8px' }}>
                                {player1 ? (
                                  <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                    {selectedTournament?.format === 'DOUBLES' && player1.name.includes(' / ') ? (
                                      <div className="font-medium text-gray-900">
                                        <div className="text-center" style={{ fontSize: '12px' }}>{player1.name.split(' / ')[0]}</div>
                                        <div className="text-center" style={{ fontSize: '12px', marginTop: '2px' }}>{player1.name.split(' / ')[1]}</div>
                                      </div>
                                    ) : (
                                      <div className="font-medium text-gray-900">
                                        <div className="text-center" style={{ fontSize: '12px' }}>{player1.name}</div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>—</div>
                                )}
                              </div>
                              <div className={`rounded ${player2 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`} style={{ padding: '6px 8px' }}>
                                {player2 ? (
                                  <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                    {selectedTournament?.format === 'DOUBLES' && player2.name.includes(' / ') ? (
                                      <div className="font-medium text-gray-900">
                                        <div className="text-center" style={{ fontSize: '12px' }}>{player2.name.split(' / ')[0]}</div>
                                        <div className="text-center" style={{ fontSize: '12px', marginTop: '2px' }}>{player2.name.split(' / ')[1]}</div>
                                      </div>
                                    ) : (
                                      <div className="font-medium text-gray-900">
                                        <div className="text-center" style={{ fontSize: '12px' }}>{player2.name}</div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>—</div>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Отображение счета справа от ячеек */}
                          {player1GamesScore !== null && player2GamesScore !== null && (
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center" style={{ right: '-20px', width: '52px', padding: '0 8px', boxSizing: 'border-box' }}>
                              <div className="flex items-start" style={{ gap: '2px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>{player1GamesScore}</span>
                                {player1TiebreakScore !== null && (
                                  <span style={{ fontSize: '9px', color: '#4b5563' }}>{player1TiebreakScore}</span>
                                )}
                              </div>
                              <div className="flex items-start" style={{ gap: '2px', marginTop: '4px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>{player2GamesScore}</span>
                                {player2TiebreakScore !== null && (
                                  <span style={{ fontSize: '9px', color: '#4b5563' }}>{player2TiebreakScore}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Короткая горизонтальная линия справа */}
                          <div 
                            className="absolute h-0.5 bg-gray-300 z-0" 
                            style={{ 
                              left: '0px',
                              top: 'calc(50% - 1px)',
                              transform: 'translateY(-50%)',
                              width: '215px'
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
          <div
            className="flex flex-wrap justify-center relative"
            style={{
              gap: '96px',
              width: '100%',
              paddingLeft: `${bracketSideInsetPx}px`,
              paddingRight: `${bracketSideInsetPx}px`,
              boxSizing: 'border-box',
              transform: applyManualPlayoffTweaks && isPlayoff32
                ? 'translateX(250px)'
                : (applyManualPlayoffTweaks && isPlayoff16 ? 'translateX(270px)' : undefined)
            }}
          >
            {playoffStage.rounds.map((round, roundIndex) => {
              const matchesInRound = round.matches;
              const matches = [];
              
              // roundIndex соответствует реальному индексу раунда
              const realRoundIndex = roundIndex;
              
              // Генерируем матчи для раунда
              if (realRoundIndex === 0) {
                // Первый раунд (полуфинал) - используем сохраненную жеребьёвку
                // Для полуфинала с 2 матчами: матч 1 = позиции 1 и 3, матч 2 = позиции 2 и 4
                // Для 1/4 финала с 4 матчами: матч 1 = позиции 1 и 2, матч 2 = позиции 3 и 4, и т.д.
                
                if (playoffDrawEntries.length > 0) {
                  const playersBySlot = new Map<number, PlayoffPlayer>();
                  playoffPlayers.forEach((player) => {
                    if (typeof player.playoffSlot === 'number') {
                      playersBySlot.set(player.playoffSlot, player);
                    }
                  });
                  
                  // Для всех раундов используем последовательные пары (крест на крест)
                  // Для полуфинала: матч 1 = позиции 1 и 2, матч 2 = позиции 3 и 4
                  // Для 1/4 финала: матч 1 = позиции 1 и 2, матч 2 = позиции 3 и 4, и т.д.
                  
                  for (let i = 0; i < matchesInRound; i++) {
                    // Используем пары слотов 1-2, 3-4, 5-6, 7-8 ...
                    const slot1 = i * 2 + 1;
                    const slot2 = i * 2 + 2;
                    
                    // Ищем соответствующий матч плей-оффа
                    const playoffMatch = getPlayoffMatchByStageAndSlot(round, i + 1, i + 1);
                    
                    matches.push({
                      matchNumber: i + 1,
                      player1: playersBySlot.get(slot1) || null,
                      player2: playersBySlot.get(slot2) || null,
                      match: playoffMatch || null, // Добавляем найденный матч
                    });
                  }
                } else {
                  // Если жеребьёвка плей-офф не сохранена отдельными playoff-* слотами,
                  // используем слоты из уже созданных матчей первого раунда.
                  const playersBySlot = new Map<number, PlayoffPlayer>();
                  playoffPlayers.forEach((player) => {
                    if (typeof player.playoffSlot === 'number') {
                      playersBySlot.set(player.playoffSlot, player);
                    }
                  });

                  for (let i = 0; i < matchesInRound; i++) {
                    const playoffMatch = getPlayoffMatchByStageAndSlot(round, i + 1, i + 1);
                    const slot1 = i * 2 + 1;
                    const slot2 = i * 2 + 2;

                    // Приоритет: участники из реального матча (если есть),
                    // затем fallback в playersBySlot.
                    const player1FromMatch = playoffMatch?.player1Id
                      ? (playoffPlayers.find((p: PlayoffPlayer) => p.id === playoffMatch.player1Id) || null)
                      : null;
                    const player2FromMatch = playoffMatch?.player2Id
                      ? (playoffPlayers.find((p: PlayoffPlayer) => p.id === playoffMatch.player2Id) || null)
                      : null;

                    matches.push({
                      matchNumber: i + 1,
                      player1: player1FromMatch || playersBySlot.get(slot1) || null,
                      player2: player2FromMatch || playersBySlot.get(slot2) || null,
                      match: playoffMatch || null,
                    });
                  }
                }
              } else {
                // Последующие раунды - матчи с победителями из предыдущего раунда
                // Определяем номер первого матча текущего раунда
                // Если первый раунд имел 2 матча (Playoff-1, Playoff-2), то второй раунд начинается с Playoff-3
                let firstMatchNumber = 1;
                for (let prevRoundIndex = 0; prevRoundIndex < realRoundIndex; prevRoundIndex++) {
                  firstMatchNumber += playoffStage.rounds[prevRoundIndex].matches;
                }

                const resolveWinnerFromMatch = (sourceMatch: Match | undefined): PlayoffPlayer | null => {
                  if (!sourceMatch || !sourceMatch.winnerId) return null;

                  if (selectedTournament?.format === 'DOUBLES') {
                    if (!sourceMatch.winner2Id) return null;
                    if (!(sourceMatch.player1 && sourceMatch.player2 && sourceMatch.player3 && sourceMatch.player4)) {
                      return null;
                    }

                    const team1PlayerIds = [sourceMatch.player1Id, sourceMatch.player3Id].filter(Boolean);
                    const team2PlayerIds = [sourceMatch.player2Id, sourceMatch.player4Id].filter(Boolean);

                    const team1Won = team1PlayerIds.includes(sourceMatch.winnerId) && team1PlayerIds.includes(sourceMatch.winner2Id);
                    const team2Won = team2PlayerIds.includes(sourceMatch.winnerId) && team2PlayerIds.includes(sourceMatch.winner2Id);

                    if (team1Won) {
                      const team1Name = `${sourceMatch.player1.name} / ${sourceMatch.player3.name}`;
                      return playoffPlayers.find((p: PlayoffPlayer) => p.name === team1Name) || null;
                    }
                    if (team2Won) {
                      const team2Name = `${sourceMatch.player2.name} / ${sourceMatch.player4.name}`;
                      return playoffPlayers.find((p: PlayoffPlayer) => p.name === team2Name) || null;
                    }

                    return null;
                  }

                  return playoffPlayers.find((p: PlayoffPlayer) => p.id === sourceMatch.winnerId) || null;
                };

                for (let i = 0; i < matchesInRound; i++) {
                  // Для legacy-матчей (без stageMatchNumber/roundName) нужен fallback по Playoff-N.
                  // Функция сама отключит этот fallback для R32.
                  const playoffMatch = getPlayoffMatchByStageAndSlot(round, i + 1, firstMatchNumber + i);
                  
                  // Определяем игроков из предыдущего раунда (победители)
                  // Для этого нужно найти матчи предыдущего раунда и их победителей
                  let player1: PlayoffPlayer | null = null;
                  let player2: PlayoffPlayer | null = null;
                  
                  // Если матч финала уже создан, используем его данные для определения команд
                  if (playoffMatch && selectedTournament?.format === 'DOUBLES') {
                    // Для парных: ищем команды по player1Id/player3Id и player2Id/player4Id
                    if (playoffMatch.player1Id && playoffMatch.player3Id) {
                      // Ищем команду, которая содержит player1Id и player3Id
                      // В playoffPlayers для парных p.id - это teamId, но нам нужно найти команду по playerId
                      // Для этого нужно проверить, какие команды есть в playoffPlayers
                      // Но проблема в том, что в playoffPlayers нет информации о playerId команды
                      // Поэтому используем другой подход: ищем команду по имени из матча
                      if (playoffMatch.player1 && playoffMatch.player3) {
                        const team1Name = `${playoffMatch.player1.name} / ${playoffMatch.player3.name}`;
                        player1 = playoffPlayers.find((p: PlayoffPlayer) => 
                          p.name === team1Name
                        ) || null;
                      }
                    }
                    
                    if (playoffMatch.player2Id && playoffMatch.player4Id) {
                      if (playoffMatch.player2 && playoffMatch.player4) {
                        const team2Name = `${playoffMatch.player2.name} / ${playoffMatch.player4.name}`;
                        player2 = playoffPlayers.find((p: PlayoffPlayer) => 
                          p.name === team2Name
                        ) || null;
                      }
                    }
                  } else if (playoffMatch && selectedTournament?.format !== 'DOUBLES') {
                    // Для одиночных: ищем игрока по playerId
                    if (playoffMatch.player1Id) {
                      player1 = playoffPlayers.find((p: PlayoffPlayer) => 
                        p.id === playoffMatch.player1Id
                      ) || null;
                    }
                    if (playoffMatch.player2Id) {
                      player2 = playoffPlayers.find((p: PlayoffPlayer) => 
                        p.id === playoffMatch.player2Id
                      ) || null;
                    }
                  }
                  
                  // Подставляем победителей из предыдущего раунда по мере появления результатов:
                  // даже если второй участник пары еще не определен.
                  if ((!player1 || !player2) && realRoundIndex > 0) {
                    const prevRoundMatches = playoffStage.rounds[realRoundIndex - 1].matches;
                    const prevRoundFirstMatchNumber = firstMatchNumber - prevRoundMatches;

                    const prevRound = playoffStage.rounds[realRoundIndex - 1];
                    const prevMatch1 = getPlayoffMatchByStageAndSlot(
                      prevRound,
                      i * 2 + 1,
                      prevRoundFirstMatchNumber + i * 2,
                      false
                    );
                    const prevMatch2 = getPlayoffMatchByStageAndSlot(
                      prevRound,
                      i * 2 + 2,
                      prevRoundFirstMatchNumber + i * 2 + 1,
                      false
                    );

                    if (!player1) {
                      player1 = resolveWinnerFromMatch(prevMatch1);
                    }
                    if (!player2) {
                      player2 = resolveWinnerFromMatch(prevMatch2);
                    }
                  }
                  
                  matches.push({
                    matchNumber: i + 1,
                    player1: player1,
                    player2: player2,
                    match: playoffMatch || null,
                  });
                }
              }

              // Вычисляем общую высоту для выравнивания между раундами
              const matchHeight = 90; // Примерная высота одного матча (уменьшено для компактности)
              const gapBetweenMatches = 80; // Зазор между матчами в первом раунде
              const firstRoundMatches = playoffStage.rounds[0].matches;
              const firstRoundHeight = firstRoundMatches * matchHeight + (firstRoundMatches - 1) * gapBetweenMatches;
              const playoffColumnsMinHeight = firstRoundHeight + 35;
              
              // Определяем порядок отображения: "Полуфинал" слева (order: 1), "Финал" справа (order: 2)
              // В массиве rounds[0] - это полуфинал (realRoundIndex === 0), rounds[1] - это финал
              // Но визуально нужно показать полуфинал слева, финал справа
              const isSemifinal = realRoundIndex === 0; // Первый раунд в массиве - это полуфинал
              const displayOrder = isSemifinal ? 1 : 2;
              const isSe4Layout = playoffStage.rounds[0]?.matches === 2;
              // В SE-4 фактическая высота карточки 1-го раунда отличается по формату:
              // - Singles: чуть выше базового расчета из-за meta-строк (берем 120px для точного визуального центра)
              // - Doubles: выше за счет двух строк состава команды (126px)
              const se4FirstRoundCardHeight = selectedTournament?.format === 'DOUBLES' ? 126 : 120;
              const se4FirstRoundCenterY = se4FirstRoundCardHeight / 2;
              const se4TopSemifinalLocalCenter = se4MeasuredSemifinalLocalCenters[0] ?? se4FirstRoundCenterY;
              const se4BottomSemifinalLocalCenter = se4MeasuredSemifinalLocalCenters[1] ?? se4FirstRoundCenterY;
              const se4TopSemifinalCenterY = se4TopSemifinalLocalCenter;
              const se4BottomSemifinalCenterY = matchHeight + gapBetweenMatches + se4BottomSemifinalLocalCenter;
              const se4BridgeToFinalY = (se4TopSemifinalCenterY + se4BottomSemifinalCenterY) / 2;
              
              // Ширина ячеек: первый этап (полуфинал) шире из-за отображения группы и места
              const cellWidth = realRoundIndex === 0 ? '230px' : '200px';
              const isSemifinalPlayoffLayout = playoffStage.rounds[0]?.matches === 2;
              const headingTopOffset = isSemifinalPlayoffLayout
                ? '0px'
                : (
                    round.name === 'Semifinale'
                      ? '95px'
                      : round.name === 'Finala'
                        ? '260px'
                        : '0px'
                  );
              const se16FinalHeadingShift =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8 && round.matches === 1
                  ? ' translate(20px, -175px)'
                  : '';
              const se16SemifinalHeadingXShift =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8 && round.name === 'Semifinale'
                  ? ' translateX(5px)'
                  : '';
              const se16QuarterHeadingXShift =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8 && round.name === 'Sferturi de finală'
                  ? ' translateX(-20px)'
                  : '';
              const r32HeadingXShift =
                applyManualPlayoffTweaks && round.matches === 16 && realRoundIndex === 0
                  ? ' translateX(50px)'
                  : '';
              const optimiHeadingXShift =
                applyManualPlayoffTweaks && round.matches === 8 && playoffStage.rounds[0]?.matches === 16
                  ? ' translateX(-10px)'
                  : '';
              const optimiHeadingExtraYOffset =
                applyManualPlayoffTweaks && round.matches === 8 && playoffStage.rounds[0]?.matches === 16
                  ? 85
                  : 0;
              const sferturiHeadingExtraYOffset =
                applyManualPlayoffTweaks && round.matches === 4 && playoffStage.rounds[0]?.matches === 16
                  ? 255
                  : 0;
              const sferturiHeadingXShift =
                applyManualPlayoffTweaks && round.matches === 4 && playoffStage.rounds[0]?.matches === 16
                  ? ' translateX(-15px)'
                  : '';
              const semifinaleHeadingXShift =
                applyManualPlayoffTweaks && round.matches === 2 && playoffStage.rounds[0]?.matches === 16
                  ? ' translateX(10px)'
                  : '';
              const semifinaleHeadingExtraYOffset =
                applyManualPlayoffTweaks && round.matches === 2 && playoffStage.rounds[0]?.matches === 16
                  ? 505
                  : 0;
              const finalaHeadingXShift =
                applyManualPlayoffTweaks && round.matches === 1 && playoffStage.rounds[0]?.matches === 16
                  ? ' translateX(25px)'
                  : '';
              const finalaHeadingExtraYOffset =
                applyManualPlayoffTweaks && round.matches === 1 && playoffStage.rounds[0]?.matches === 16
                  ? -170
                  : 0;
              const se16HeadingExtraYOffset =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8
                  ? (
                      round.name === 'Sferturi de finală'
                        ? 100
                        : (round.name === 'Semifinale' ? 170 : 0)
                    )
                  : 0;
              const se16SferturiLiftPx =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8 && round.matches === 4
                  ? -10
                  : 0;
              const se16FinalLiftPx =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8 && round.matches === 1
                  ? -10
                  : 0;
              const se8FinalLiftPx =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 4 && round.matches === 1
                  ? -10
                  : 0;
              const se16RoundsLeftShift =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8
                  ? (
                      realRoundIndex <= 2
                        ? 'translateX(-170px)'
                        : (realRoundIndex === 3 ? 'translateX(-50px)' : 'none')
                    )
                  : 'none';
              const r32RoundLeftShift =
                applyManualPlayoffTweaks && round.matches === 16 && realRoundIndex === 0
                  ? 'translateX(-280px)'
                  : 'none';
              const r16RoundLeftShift =
                applyManualPlayoffTweaks && round.matches === 8 && realRoundIndex === 1
                  ? 'translateX(-280px)'
                  : 'none';
              const qfRoundLeftShift =
                applyManualPlayoffTweaks && round.matches === 4 && realRoundIndex === 2
                  ? 'translateX(-280px)'
                  : 'none';
              const sfRoundRightShift =
                applyManualPlayoffTweaks && round.matches === 2 && realRoundIndex === 3
                  ? 'translateX(-282px)'
                  : 'none';
              const finalRoundRightShift =
                applyManualPlayoffTweaks && round.matches === 1 && realRoundIndex === 4
                  ? 'translate(-282px, 1195px)'
                  : 'none';
              const se16FinalRoundShift =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8 && round.matches === 1
                  ? 'translate(-170px, 525px)'
                  : 'none';
              const columnShiftTransform =
                r32RoundLeftShift !== 'none'
                  ? r32RoundLeftShift
                  : (
                      r16RoundLeftShift !== 'none'
                        ? r16RoundLeftShift
                        : (
                            qfRoundLeftShift !== 'none'
                              ? qfRoundLeftShift
                              : (
                                  sfRoundRightShift !== 'none'
                                    ? sfRoundRightShift
                                    : (
                                        finalRoundRightShift !== 'none'
                                          ? finalRoundRightShift
                                          : (se16FinalRoundShift !== 'none' ? se16FinalRoundShift : se16RoundsLeftShift)
                                      )
                                )
                          )
                    );
              
              return (
                <div
                  key={round.round}
                  className="flex flex-col items-center relative"
                  style={{ order: displayOrder, transform: columnShiftTransform }}
                >
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', textAlign: 'center', color: '#374151', transform: `translateY(calc(${headingTopOffset} + ${se16HeadingExtraYOffset + optimiHeadingExtraYOffset + sferturiHeadingExtraYOffset + semifinaleHeadingExtraYOffset + finalaHeadingExtraYOffset + se16SferturiLiftPx + se16FinalLiftPx + se8FinalLiftPx}px))${optimiHeadingXShift}${sferturiHeadingXShift}${semifinaleHeadingXShift}${finalaHeadingXShift}${r32HeadingXShift}${se16QuarterHeadingXShift}${se16SemifinalHeadingXShift}${se16FinalHeadingShift}` }}>
                    {getPlayoffRoundDisplayName(round)}
                  </h3>
                  <div
                    className="relative flex-shrink-0"
                    style={{ minHeight: `${playoffColumnsMinHeight}px`, width: cellWidth }}
                  >
                    {matches.map((match, matchIndex) => {
                      const isLastRound = realRoundIndex === playoffStage.rounds.length - 1;
                      const shouldHaveConnection = !isLastRound;
                      const isQuarterPlayoff = playoffStage.rounds[0]?.matches === 4;
                      const isSinglesQuarterfinal =
                        selectedTournament?.format !== 'DOUBLES' &&
                        realRoundIndex === 0 &&
                        isQuarterPlayoff;
                      const isSinglesRoundOf16Layout =
                        selectedTournament?.format !== 'DOUBLES' &&
                        realRoundIndex === 0 &&
                        playoffStage.rounds[0]?.matches === 8;
                      const showFinalOutgoingLine =
                        isLastRound &&
                        !!(match.match && match.match.result) &&
                        !isQuarterPlayoff &&
                        playoffStage.rounds[0]?.matches !== 2;
                      const isSe4SemifinalRound = isSe4Layout && realRoundIndex === 0;
                      const isSe4FinalMatchCard = isSe4Layout && isLastRound && matchIndex === 0;
                      
                      // Вычисляем позицию матча: он должен быть между двумя матчами предыдущего раунда
                      let topOffset = 0;
                      if (realRoundIndex > 0) {
                        const prevRoundMatches = playoffStage.rounds[realRoundIndex - 1].matches;
                        // Каждый матч в текущем раунде находится между двумя матчами предыдущего
                        // Матч 0 находится между матчами 0 и 1 предыдущего раунда
                        // Матч 1 находится между матчами 2 и 3 предыдущего раунда
                        const prevMatch1Index = matchIndex * 2;
                        const prevMatch2Index = matchIndex * 2 + 1;
                        const prevMatch1Top = prevMatch1Index * (matchHeight + gapBetweenMatches);
                        const prevMatch2Top = prevMatch2Index * (matchHeight + gapBetweenMatches);
                        let prevMatch1Center = prevMatch1Top + matchHeight / 2;
                        let prevMatch2Center = prevMatch2Top + matchHeight / 2;
                        if (isSe4Layout && realRoundIndex === 1) {
                          prevMatch1Center = prevMatch1Top + se4TopSemifinalLocalCenter;
                          prevMatch2Center = prevMatch2Top + se4BottomSemifinalLocalCenter;
                        }
                        // Позиция между центрами двух матчей
                        const centerBetweenMatches = (prevMatch1Center + prevMatch2Center) / 2;
                        // Выравниваем центр текущего матча с центром между предыдущими
                        const currentRoundCenterY =
                          isSe4Layout && realRoundIndex === 1
                            ? (se4MeasuredFinalLocalCenter ?? matchHeight / 2)
                            : matchHeight / 2;
                        topOffset = centerBetweenMatches - currentRoundCenterY;
                        // Для Single Elimination - 8 чуть опускаем оба полуфинала.
                        if (realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 4) {
                          topOffset += 10;
                        }
                        // Для Single Elimination - 8 опускаем финал ниже.
                        if (isLastRound && playoffStage.rounds[0]?.matches === 4) {
                          topOffset += 180;
                        }
                        // Для SE-16: четвертьфиналы (2-й раунд отображения) опускаем чуть ниже.
                        if (applyManualPlayoffTweaks && realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 8) {
                          topOffset += 15;
                        }
                        if (applyManualPlayoffTweaks && realRoundIndex === 2 && matchIndex === 3 && playoffStage.rounds[0]?.matches === 16) {
                          topOffset += 1190;
                        }
                        if (applyManualPlayoffTweaks && realRoundIndex === 2 && matchIndex === 1 && playoffStage.rounds[0]?.matches === 16) {
                          topOffset += 510;
                        }
                        if (applyManualPlayoffTweaks && realRoundIndex === 2 && matchIndex === 0 && playoffStage.rounds[0]?.matches === 16) {
                          topOffset += 170;
                        }
                        if (applyManualPlayoffTweaks && realRoundIndex === 2 && matchIndex === 2 && playoffStage.rounds[0]?.matches === 16) {
                          topOffset += 850;
                        }
                        if (applyManualPlayoffTweaks && realRoundIndex === 3 && matchIndex === 1 && playoffStage.rounds[0]?.matches === 16) {
                          topOffset += 1540;
                        }
                        if (applyManualPlayoffTweaks && realRoundIndex === 3 && matchIndex === 0 && playoffStage.rounds[0]?.matches === 16) {
                          topOffset += 515;
                        }
                        // Для SE-16: разводим полуфиналы по высоте
                        // (1-й выше на 200px, 2-й ниже на 200px от базового +400).
                        if (applyManualPlayoffTweaks && realRoundIndex === 2 && playoffStage.rounds[0]?.matches === 8) {
                          topOffset += matchIndex === 0 ? 180 : 520;
                        }
                      } else {
                        // Для первого раунда просто последовательное расположение
                        topOffset = matchIndex * (matchHeight + gapBetweenMatches);
                      }
                      if (se16SferturiLiftPx !== 0) {
                        topOffset += se16SferturiLiftPx;
                      }
                      if (se16FinalLiftPx !== 0) {
                        topOffset += se16FinalLiftPx;
                      }
                      if (se8FinalLiftPx !== 0) {
                        topOffset += se8FinalLiftPx;
                      }

                      const baseScoreRightOffset =
                        realRoundIndex === 0
                          ? -33
                          : (realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 4 ? -33 : 0);
                      const se16ScoreShiftRight =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8 && (realRoundIndex === 1 || realRoundIndex === 2)
                          ? 33
                          : 0;
                      const r32OptimiScoreShiftRight =
                        playoffStage.rounds[0]?.matches === 16 && realRoundIndex === 1
                          ? -35
                          : 0;
                      const r32SferturiScoreShiftRight =
                        playoffStage.rounds[0]?.matches === 16 && realRoundIndex === 2
                          ? -35
                          : 0;
                      const r32SemifinaleScoreShiftRight =
                        playoffStage.rounds[0]?.matches === 16 && realRoundIndex === 3
                          ? -35
                          : 0;
                      // Сдвиг счетов влево на 5px для всех раундов (QF/SF/Final).
                      // Для финала сохраняем дополнительный вынос вправо, но тоже с учетом -5px влево.
                      const scoreRightOffset = isLastRound
                        ? `${baseScoreRightOffset - 24}px`
                        : `${baseScoreRightOffset + 8 - se16ScoreShiftRight + r32OptimiScoreShiftRight + r32SferturiScoreShiftRight + r32SemifinaleScoreShiftRight}px`;
                      const showPlayer1Meta = shouldShowPlayoffSeedMeta(match.player1);
                      const showPlayer2Meta = shouldShowPlayoffSeedMeta(match.player2);
                      const player1GroupLabel = showPlayer1Meta ? getCompactGroupLabel(match.player1?.groupName || '') : '';
                      const player1PlaceLabel = showPlayer1Meta && Number(match.player1?.position || 0) > 0 ? String(match.player1?.position) : '';
                      const player2GroupLabel = showPlayer2Meta ? getCompactGroupLabel(match.player2?.groupName || '') : '';
                      const player2PlaceLabel = showPlayer2Meta && Number(match.player2?.position || 0) > 0 ? String(match.player2?.position) : '';
                      const se16FinalMatchShift =
                        'none';
                      const matchEntity: any = match.match || null;
                      const isDoublesMatch = selectedTournament?.format === 'DOUBLES';
                      const matchStatus = String(matchEntity?.status || '').toUpperCase();
                      const isCompletedMatch = matchStatus === 'COMPLETED';
                      const side1Ids = [matchEntity?.player1Id, matchEntity?.player3Id].filter(Boolean) as string[];
                      const side2Ids = [matchEntity?.player2Id, matchEntity?.player4Id].filter(Boolean) as string[];
                      const side1Won = isDoublesMatch
                        ? (!!matchEntity?.winnerId &&
                            side1Ids.includes(matchEntity.winnerId) &&
                            (!matchEntity?.winner2Id || side1Ids.includes(matchEntity.winner2Id)))
                        : (!!matchEntity?.winnerId && matchEntity.winnerId === matchEntity.player1Id);
                      const side2Won = isDoublesMatch
                        ? (!!matchEntity?.winnerId &&
                            side2Ids.includes(matchEntity.winnerId) &&
                            (!matchEntity?.winner2Id || side2Ids.includes(matchEntity.winner2Id)))
                        : (!!matchEntity?.winnerId && matchEntity.winnerId === matchEntity.player2Id);
                      const player1IsBye =
                        !match.player1 &&
                        !!matchEntity &&
                        isCompletedMatch &&
                        (isDoublesMatch
                          ? (side1Ids.length === 0 && side2Ids.length > 0 && side2Won)
                          : (!matchEntity.player1Id && !!matchEntity.player2Id && side2Won));
                      const player2IsBye =
                        !match.player2 &&
                        !!matchEntity &&
                        isCompletedMatch &&
                        (isDoublesMatch
                          ? (side2Ids.length === 0 && side1Ids.length > 0 && side1Won)
                          : (!matchEntity.player2Id && !!matchEntity.player1Id && side1Won));
                      const player1Placeholder = player1IsBye ? 'X' : '—';
                      const player2Placeholder = player2IsBye ? 'X' : '—';
                      
                      return (
                        <div 
                          key={matchIndex} 
                          className="absolute left-0 flex items-center"
                          style={{ top: `${topOffset}px`, width: '100%', transform: se16FinalMatchShift }}
                        >
                          <div className="relative flex items-center w-full pr-[28px]">
                            {(() => {
                              // Извлекаем счет для каждого игрока из результата матча
                              type ScoreToken = { games: string; tiebreak: string | null };
                              let player1GamesScore: string | null = null;
                              let player2GamesScore: string | null = null;
                              let player1TiebreakScore: string | null = null;
                              let player2TiebreakScore: string | null = null;
                              let player1ScoreTokens: ScoreToken[] = [];
                              let player2ScoreTokens: ScoreToken[] = [];
                              let statusOnlyScore: string | null = null;
                              
                              if (match.match && match.match.result) {
                                try {
                                  const result: MatchResult = JSON.parse(match.match.result);
                                  if (result.sets && result.sets.length > 0) {
                                    // Показываем полноценный счет по всем введенным сетам
                                    // (для форматов вроде "2 сета + супер-тайбрейк").
                                    const playedSets = result.sets.filter((set) => {
                                      const hasGames = (set.player1 > 0 || set.player2 > 0);
                                      const hasTiebreak = set.tiebreak !== null && set.tiebreak !== undefined;
                                      return hasGames || hasTiebreak;
                                    });

                                    const setsToShow = playedSets.length > 0 ? playedSets : result.sets;
                                    if (setsToShow.length > 0) {
                                      player1ScoreTokens = setsToShow.map((set) => {
                                        let p1Tiebreak: string | null = null;
                                        if (!set.superTiebreak && set.tiebreak !== null && set.tiebreak !== undefined) {
                                          if (typeof set.tiebreak === 'object') {
                                            const tie = set.tiebreak as { player1?: number; player2?: number };
                                            if (typeof tie.player1 === 'number') {
                                              p1Tiebreak = tie.player1.toString();
                                            }
                                          } else {
                                            const tie = Number(set.tiebreak);
                                            if (!Number.isNaN(tie) && set.player1 > set.player2) {
                                              p1Tiebreak = tie.toString();
                                            } else if (!Number.isNaN(tie) && set.player2 > set.player1) {
                                              p1Tiebreak = Math.max(tie - 2, 0).toString();
                                            }
                                          }
                                        }
                                        return { games: set.player1.toString(), tiebreak: p1Tiebreak };
                                      });

                                      player2ScoreTokens = setsToShow.map((set) => {
                                        let p2Tiebreak: string | null = null;
                                        if (!set.superTiebreak && set.tiebreak !== null && set.tiebreak !== undefined) {
                                          if (typeof set.tiebreak === 'object') {
                                            const tie = set.tiebreak as { player1?: number; player2?: number };
                                            if (typeof tie.player2 === 'number') {
                                              p2Tiebreak = tie.player2.toString();
                                            }
                                          } else {
                                            const tie = Number(set.tiebreak);
                                            if (!Number.isNaN(tie) && set.player2 > set.player1) {
                                              p2Tiebreak = tie.toString();
                                            } else if (!Number.isNaN(tie) && set.player1 > set.player2) {
                                              p2Tiebreak = Math.max(tie - 2, 0).toString();
                                            }
                                          }
                                        }
                                        return { games: set.player2.toString(), tiebreak: p2Tiebreak };
                                      });

                                      player1GamesScore = player1ScoreTokens.map((token) => token.games).join(' ');
                                      player2GamesScore = player2ScoreTokens.map((token) => token.games).join(' ');
                                    }

                                    // Отдельные superscript-тайбрейки отключаем:
                                    // их выводим рядом с каждым сетом (как в матче за 3-е место).
                                    player1TiebreakScore = null;
                                    player2TiebreakScore = null;
                                  }

                                  const hasPlayedSets =
                                    Array.isArray(result.sets) &&
                                    result.sets.some((set) => {
                                      const hasGames = set.player1 > 0 || set.player2 > 0;
                                      const hasTiebreak = set.tiebreak !== null && set.tiebreak !== undefined;
                                      return hasGames || hasTiebreak;
                                    });

                                  const hasWalkoverLikeSpecialStatus =
                                    !!result.specialStatus &&
                                    AUTO_SPECIAL_STATUS_TYPES.has(result.specialStatus.type);

                                  const hasSpecialStatusWithoutSets =
                                    !hasPlayedSets &&
                                    (result.walkover === true || hasWalkoverLikeSpecialStatus);

                                  // Если сеты не сыграны и исход walkover-like, в плей-офф показываем только W.O.
                                  // Для SUSPENSION и других не walkover-like статусов W.O. не подставляем.
                                  if (hasSpecialStatusWithoutSets) {
                                    statusOnlyScore = 'W.O.';
                                  }
                                } catch (e) {
                                  // Игнорируем ошибки парсинга
                                }
                              }

                              // Fallback для спецстатусов (INJURY/NO_SHOW/REFUSAL и т.д.):
                              // в таких матчах сеты могут быть пустыми, но результат уже определен.
                              if (
                                !statusOnlyScore &&
                                player1ScoreTokens.length === 0 &&
                                player2ScoreTokens.length === 0 &&
                                match.match
                              ) {
                                const formatted = formatMatchScoreForTable(match.match);
                                const setMatch = formatted.setsScore.match(/^(\d+):(\d+)/);
                                if (setMatch) {
                                  player1ScoreTokens = [{ games: setMatch[1], tiebreak: null }];
                                  player2ScoreTokens = [{ games: setMatch[2], tiebreak: null }];
                                  player1GamesScore = setMatch[1];
                                  player2GamesScore = setMatch[2];
                                }
                              }

                              const matchScheduleLabel = getPlayoffMatchScheduleLabel(match.match, hideScheduleMetaForLevel);
                              
                              return (
                                <>
                                  {matchScheduleLabel && (
                                    <div
                                      className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap"
                                      style={{ top: '-18px', fontSize: '11px', color: '#6b7280', fontWeight: 500 }}
                                    >
                                      {matchScheduleLabel}
                                    </div>
                                  )}
                                  <div
                                    className="border-2 border-gray-300 rounded-lg bg-white shadow-md relative z-10 flex-1"
                                    ref={(node) => {
                                      if (isSe4SemifinalRound) {
                                        se4SemifinalCardRefs.current[matchIndex] = node;
                                      }
                                      if (isSe4FinalMatchCard) {
                                        se4FinalCardRef.current = node;
                                      }
                                    }}
                                    style={{ padding: '8px', minHeight: '90px', width: cellWidth }}
                                  >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: realRoundIndex === 0 ? 'center' : 'flex-start', minHeight: '100%' }}>
                                      <div
                                        className={`rounded ${match.player1 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}
                                        style={{
                                          padding: realRoundIndex === 0 ? '6px 8px' : '6px 8px',
                                          minHeight: realRoundIndex === 0 ? '45px' : '32px',
                                          display: realRoundIndex === 0 ? 'flex' : (isSinglesQuarterfinal ? 'flex' : undefined),
                                          alignItems: realRoundIndex === 0 ? 'center' : (isSinglesQuarterfinal ? 'center' : undefined),
                                          justifyContent: realRoundIndex === 0 ? 'center' : undefined
                                        }}
                                      >
                                        {match.player1 ? (
                                          <div style={{ fontSize: '12px', lineHeight: '1.5', width: '100%' }}>
                                            {selectedTournament?.format === 'DOUBLES' && match.player1.name.includes(' / ') ? (
                                              <div className="font-medium text-gray-900">
                                                {realRoundIndex === 0 ? (
                                                  <>
                                                    <div className="flex items-center" style={{ gap: '2px' }}>
                                                      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', display: 'inline-block', width: '28px', textAlign: 'center' }}>{player1GroupLabel}</span>
                                                      <span className="flex-1 text-center" style={{ fontSize: '12px' }}>{match.player1.name.split(' / ')[0]}</span>
                                                    </div>
                                                    <div className="flex items-center" style={{ gap: '2px', marginTop: '2px' }}>
                                                      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', display: 'inline-block', width: '28px', textAlign: 'center' }}>{player1PlaceLabel}</span>
                                                      <span className="flex-1 text-center" style={{ fontSize: '12px' }}>{match.player1.name.split(' / ')[1]}</span>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <>
                                                    <div className="text-center" style={{ fontSize: '12px' }}>{match.player1.name.split(' / ')[0]}</div>
                                                    <div className="text-center" style={{ fontSize: '12px', marginTop: '2px' }}>{match.player1.name.split(' / ')[1]}</div>
                                                  </>
                                                )}
                                              </div>
                                            ) : (
                                              <div
                                                className="font-medium text-gray-900"
                                                style={{
                                                  display: isSinglesQuarterfinal ? 'flex' : undefined,
                                                  flexDirection: isSinglesQuarterfinal ? 'column' : undefined,
                                                  justifyContent: isSinglesQuarterfinal ? 'center' : undefined,
                                                  minHeight: isSinglesQuarterfinal ? '100%' : undefined,
                                                  width: isSinglesQuarterfinal ? '100%' : undefined
                                                }}
                                              >
                                                {isSinglesQuarterfinal ? (
                                                  <div
                                                    style={{
                                                      width: '100%',
                                                      display: 'grid',
                                                      gridTemplateColumns: showPlayer1Meta ? '28px 1fr' : '1fr',
                                                      alignItems: 'center',
                                                      columnGap: '2px'
                                                    }}
                                                  >
                                                    {showPlayer1Meta && (
                                                      <div
                                                        style={{
                                                          width: '28px',
                                                          display: 'flex',
                                                          flexDirection: 'column',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          lineHeight: 1.1
                                                        }}
                                                      >
                                                        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal' }}>{player1GroupLabel}</span>
                                                        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', marginTop: '2px' }}>{player1PlaceLabel}</span>
                                                      </div>
                                                    )}
                                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                                      <span className="text-center" style={{ fontSize: '12px' }}>{match.player1.name}</span>
                                                    </div>
                                                  </div>
                                                ) : realRoundIndex === 0 ? (
                                                  <>
                                                    <div className="flex items-center" style={{ gap: '2px' }}>
                                                      {(showPlayer1Meta || !isSinglesRoundOf16Layout) && (
                                                        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', display: 'inline-block', width: '28px', textAlign: 'center' }}>{player1GroupLabel}</span>
                                                      )}
                                                      <span className={`${showPlayer1Meta || !isSinglesRoundOf16Layout ? 'flex-1' : 'w-full'} text-center`} style={{ fontSize: '12px' }}>{match.player1.name}</span>
                                                    </div>
                                                    {(showPlayer1Meta || !isSinglesRoundOf16Layout) && (
                                                      <div className="flex items-center" style={{ gap: '2px', marginTop: '2px' }}>
                                                        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', display: 'inline-block', width: '28px', textAlign: 'center' }}>{player1PlaceLabel}</span>
                                                      </div>
                                                    )}
                                                  </>
                                                ) : (
                                                  <div
                                                    className="text-center"
                                                    style={{
                                                      fontSize: '12px',
                                                      minHeight: '28px',
                                                      width: '100%',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center'
                                                    }}
                                                  >
                                                    {match.player1.name}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div
                                            style={{
                                              fontSize: '12px',
                                              color: '#9ca3af',
                                              minHeight: '28px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              position: realRoundIndex === 0 ? 'relative' : undefined,
                                              top: realRoundIndex === 0 ? '-2px' : undefined,
                                              textAlign: 'center',
                                              width: '100%'
                                            }}
                                          >
                                            {player1Placeholder}
                                          </div>
                                        )}
                                      </div>
                                      <div
                                        className={`rounded ${match.player2 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}
                                        style={{
                                          padding: realRoundIndex === 0 ? '6px 8px' : '6px 8px',
                                          minHeight: realRoundIndex === 0 ? '45px' : '32px',
                                          display: realRoundIndex === 0 ? 'flex' : (isSinglesQuarterfinal ? 'flex' : undefined),
                                          alignItems: realRoundIndex === 0 ? 'center' : (isSinglesQuarterfinal ? 'center' : undefined),
                                          justifyContent: realRoundIndex === 0 ? 'center' : undefined
                                        }}
                                      >
                                        {match.player2 ? (
                                          <div style={{ fontSize: '12px', lineHeight: '1.5', width: '100%' }}>
                                            {selectedTournament?.format === 'DOUBLES' && match.player2.name.includes(' / ') ? (
                                              <div className="font-medium text-gray-900">
                                                {realRoundIndex === 0 ? (
                                                  <>
                                                    <div className="flex items-center" style={{ gap: '2px' }}>
                                                      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', display: 'inline-block', width: '28px', textAlign: 'center' }}>{player2GroupLabel}</span>
                                                      <span className="flex-1 text-center" style={{ fontSize: '12px' }}>{match.player2.name.split(' / ')[0]}</span>
                                                    </div>
                                                    <div className="flex items-center" style={{ gap: '2px', marginTop: '2px' }}>
                                                      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', display: 'inline-block', width: '28px', textAlign: 'center' }}>{player2PlaceLabel}</span>
                                                      <span className="flex-1 text-center" style={{ fontSize: '12px' }}>{match.player2.name.split(' / ')[1]}</span>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <>
                                                    <div className="text-center" style={{ fontSize: '12px' }}>{match.player2.name.split(' / ')[0]}</div>
                                                    <div className="text-center" style={{ fontSize: '12px', marginTop: '2px' }}>{match.player2.name.split(' / ')[1]}</div>
                                                  </>
                                                )}
                                              </div>
                                            ) : (
                                              <div
                                                className="font-medium text-gray-900"
                                                style={{
                                                  display: isSinglesQuarterfinal ? 'flex' : undefined,
                                                  flexDirection: isSinglesQuarterfinal ? 'column' : undefined,
                                                  justifyContent: isSinglesQuarterfinal ? 'center' : undefined,
                                                  minHeight: isSinglesQuarterfinal ? '100%' : undefined,
                                                  width: isSinglesQuarterfinal ? '100%' : undefined
                                                }}
                                              >
                                                {isSinglesQuarterfinal ? (
                                                  <div
                                                    style={{
                                                      width: '100%',
                                                      display: 'grid',
                                                      gridTemplateColumns: showPlayer2Meta ? '28px 1fr' : '1fr',
                                                      alignItems: 'center',
                                                      columnGap: '2px'
                                                    }}
                                                  >
                                                    {showPlayer2Meta && (
                                                      <div
                                                        style={{
                                                          width: '28px',
                                                          display: 'flex',
                                                          flexDirection: 'column',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          lineHeight: 1.1
                                                        }}
                                                      >
                                                        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal' }}>{player2GroupLabel}</span>
                                                        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', marginTop: '2px' }}>{player2PlaceLabel}</span>
                                                      </div>
                                                    )}
                                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                                      <span className="text-center" style={{ fontSize: '12px' }}>{match.player2.name}</span>
                                                    </div>
                                                  </div>
                                                ) : realRoundIndex === 0 ? (
                                                  <>
                                                    <div className="flex items-center" style={{ gap: '2px' }}>
                                                      {(showPlayer2Meta || !isSinglesRoundOf16Layout) && (
                                                        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', display: 'inline-block', width: '28px', textAlign: 'center' }}>{player2GroupLabel}</span>
                                                      )}
                                                      <span className={`${showPlayer2Meta || !isSinglesRoundOf16Layout ? 'flex-1' : 'w-full'} text-center`} style={{ fontSize: '12px' }}>{match.player2.name}</span>
                                                    </div>
                                                    {(showPlayer2Meta || !isSinglesRoundOf16Layout) && (
                                                      <div className="flex items-center" style={{ gap: '2px', marginTop: '2px' }}>
                                                        <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'normal', display: 'inline-block', width: '28px', textAlign: 'center' }}>{player2PlaceLabel}</span>
                                                      </div>
                                                    )}
                                                  </>
                                                ) : (
                                                  <div
                                                    className="text-center"
                                                    style={{
                                                      fontSize: '12px',
                                                      minHeight: '28px',
                                                      width: '100%',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center'
                                                    }}
                                                  >
                                                    {match.player2.name}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div
                                            style={{
                                              fontSize: '12px',
                                              color: '#9ca3af',
                                              minHeight: '28px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              position: realRoundIndex === 0 ? 'relative' : undefined,
                                              top: realRoundIndex === 0 ? '-2px' : undefined,
                                              textAlign: 'center',
                                              width: '100%'
                                            }}
                                          >
                                            {player2Placeholder}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {/* Отображение счета справа от ячеек с командами (абсолютное позиционирование) */}
                                  {(statusOnlyScore || (player1GamesScore !== null && player2GamesScore !== null)) && (
                                    <div
                                      className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center justify-center"
                                      style={{
                                        right: scoreRightOffset,
                                        width: '52px',
                                        padding: '0 8px',
                                        boxSizing: 'border-box'
                                      }}
                                    >
                                      {statusOnlyScore ? (
                                        (() => {
                                          const currentMatch = match.match;
                                          let isPlayer1Winner = false;

                                          if (currentMatch?.winnerId) {
                                            if (selectedTournament?.format === 'DOUBLES') {
                                              const team1Ids = [currentMatch.player1Id, currentMatch.player3?.id].filter(Boolean) as string[];
                                              isPlayer1Winner = team1Ids.includes(currentMatch.winnerId);
                                            } else {
                                              isPlayer1Winner = currentMatch.winnerId === currentMatch.player1Id;
                                            }
                                          }

                                          return (
                                            <div className="flex flex-col items-center justify-center" style={{ transform: 'translateY(-8px)' }}>
                                              <div className="flex items-start justify-center" style={{ gap: '4px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>
                                                  {isPlayer1Winner ? statusOnlyScore : ''}
                                                </span>
                                              </div>
                                              <div className="flex items-start justify-center" style={{ gap: '4px', marginTop: '4px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>
                                                  {!isPlayer1Winner ? statusOnlyScore : ''}
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        })()
                                      ) : player1ScoreTokens.length > 0 && player2ScoreTokens.length > 0 ? (
                                        <div className="flex items-start justify-center" style={{ gap: '2px' }}>
                                          {player1ScoreTokens.map((token, idx) => (
                                            <div key={`set-column-${idx}`} className="flex flex-col items-center" style={{ minWidth: '14px' }}>
                                              <span className="flex items-baseline justify-center" style={{ gap: '1px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>{token.games}</span>
                                                {token.tiebreak !== null && (
                                                  <span style={{ fontSize: '9px', color: '#4b5563', lineHeight: '1', position: 'relative', top: '-5px' }}>{token.tiebreak}</span>
                                                )}
                                              </span>
                                              <span className="flex items-baseline justify-center" style={{ gap: '1px', marginTop: '4px' }}>
                                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>{player2ScoreTokens[idx]?.games ?? ''}</span>
                                                {player2ScoreTokens[idx]?.tiebreak !== null && player2ScoreTokens[idx]?.tiebreak !== undefined && (
                                                  <span style={{ fontSize: '9px', color: '#4b5563', lineHeight: '1', position: 'relative', top: '-5px' }}>{player2ScoreTokens[idx]?.tiebreak}</span>
                                                )}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <>
                                          <div className="flex items-start justify-center" style={{ gap: '4px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>{player1GamesScore}</span>
                                            {player1TiebreakScore !== null && (
                                              <span style={{ fontSize: '9px', color: '#4b5563' }}>{player1TiebreakScore}</span>
                                            )}
                                          </div>
                                          <div className="flex items-start justify-center" style={{ gap: '4px', marginTop: '4px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>{player2GamesScore}</span>
                                            {player2TiebreakScore !== null && (
                                              <span style={{ fontSize: '9px', color: '#4b5563' }}>{player2TiebreakScore}</span>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                            {/* Горизонтальная линия к следующему раунду */}
                            {/* Линия начинается от левой границы контейнера (слева от счетов) и идет вправо */}
                            {/* Для сетки с 1/4 финала отключаем правую линию от финала; для сетки с полуфиналами сохраняем */}
                            {(shouldHaveConnection || showFinalOutgoingLine) && (
                              <div
                                className="absolute h-0.5 bg-gray-300 z-0"
                                style={{
                                  left: isLastRound
                                    ? (playoffStage.rounds[0]?.matches === 8 ? '-44px' : '-44px')
                                    : (realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 16
                                        ? '-45px'
                                    : (realRoundIndex === 2 && playoffStage.rounds[0]?.matches === 16
                                        ? '-43px'
                                    : (realRoundIndex === 3 && playoffStage.rounds[0]?.matches === 16
                                        ? '-39px'
                                    : (realRoundIndex === 2 && playoffStage.rounds[0]?.matches === 8
                                        ? '-42px'
                                    : (realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 8
                                        ? '-45px'
                                        : (realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 4 ? '-44px' : '0px')))))),
                                  // Для SE-4 (полуфиналы) фиксируем ту же Y-координату,
                                  // от которой строится вертикальный коннектор.
                                  top: isSe4SemifinalRound
                                    ? `${matchIndex === 0 ? se4TopSemifinalLocalCenter : se4BottomSemifinalLocalCenter}px`
                                    : (isLastRound ? 'calc(50% - 1px)' : '50%'),
                                  transform: isSe4SemifinalRound ? 'none' : 'translateY(-50%)',
                                  width: isLastRound
                                    ? (
                                        playoffStage.rounds[0]?.matches === 8
                                          ? '280px'
                                          : (realRoundIndex === 4 && playoffStage.rounds[0]?.matches === 16 ? '275px' : '340px')
                                      )
                                    : (realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 16
                                        ? '297px'
                                    : (realRoundIndex === 2 && playoffStage.rounds[0]?.matches === 16
                                        ? '297px'
                                    : (realRoundIndex === 3 && playoffStage.rounds[0]?.matches === 16
                                        ? '292px'
                                    : (realRoundIndex === 2 && playoffStage.rounds[0]?.matches === 8
                                        ? '292px'
                                    : (realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 8
                                        ? '297px'
                                        : (realRoundIndex === 1 && playoffStage.rounds[0]?.matches === 4 ? '290px' : '282px'))))))
                                }}
                              ></div>
                            )}
                            {/* Для SE-16: отдельная горизонтальная линия, ведущая к финалу */}
                            {isLastRound && playoffStage.rounds[0]?.matches === 8 && !(match.match && match.match.result) && (
                              <div
                                className="absolute h-0.5 bg-gray-300 z-0"
                                style={{
                                  left: '-44px',
                                  top: 'calc(50% - 1px)',
                                  width: '280px',
                                  transform: 'translateY(-50%)'
                                }}
                              ></div>
                            )}
                            {isLastRound && playoffStage.rounds[0]?.matches === 16 && !(match.match && match.match.result) && (
                              <div
                                className="absolute h-0.5 bg-gray-300 z-0"
                                style={{
                                  left: '-44px',
                                  top: 'calc(50% - 1px)',
                                  width: '275px',
                                  transform: 'translateY(-50%)'
                                }}
                              ></div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* SE-4: вертикаль привязана к тем же Y, что горизонтали от полуфиналов */}
                    {realRoundIndex === 0 && matches.length === 2 && playoffStage.rounds[0]?.matches === 2 && (
                      <div 
                        className="absolute w-0.5 bg-gray-300 z-0" 
                        style={{ 
                          left: '280px', // Правый край горизонтальных линий (280px от левой границы контейнера)
                          top: `${se4TopSemifinalCenterY}px`,
                          height: `${se4BottomSemifinalCenterY - se4TopSemifinalCenterY}px`
                        }}
                      ></div>
                    )}
                    {/* Для сетки Single Elimination - 4: горизонтальная линия от вертикали полуфиналов к финалу */}
                    {realRoundIndex === 0 && matches.length === 2 && playoffStage.rounds[0]?.matches === 2 && (
                      <div
                        className="absolute h-0.5 bg-gray-300 z-0"
                        style={{
                          left: '280px',
                          // Линия в центр финальной карточки, измеренный в DOM относительно колонки полуфиналов.
                          top: `${se4BridgeToFinalY}px`,
                          width: '282px',
                          transform: 'translateY(-50%)'
                        }}
                      ></div>
                    )}
                    {/* Для сетки Single Elimination - 8: верхняя вертикальная линия (1-й и 2-й четвертьфиналы) */}
                    {realRoundIndex === 0 && matches.length === 4 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '280px',
                          top: `${matchHeight / 2 + 14}px`,
                          height: `${matchHeight + gapBetweenMatches}px`
                        }}
                      ></div>
                    )}
                    {/* Для сетки Single Elimination - 16: четыре вертикальные линии (1-2, 3-4, 5-6, 7-8) */}
                    {realRoundIndex === 0 && matches.length === 8 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '280px',
                          top: `${matchHeight / 2 + 14}px`,
                          height: `${matchHeight + gapBetweenMatches}px`
                        }}
                      ></div>
                    )}
                    {realRoundIndex === 0 && matches.length === 8 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '280px',
                          top: `${(matchHeight + gapBetweenMatches) * 2 + matchHeight / 2 + 14}px`,
                          height: `${matchHeight + gapBetweenMatches}px`
                        }}
                      ></div>
                    )}
                    {realRoundIndex === 0 && matches.length === 8 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '280px',
                          top: `${(matchHeight + gapBetweenMatches) * 4 + matchHeight / 2 + 14}px`,
                          height: `${matchHeight + gapBetweenMatches}px`
                        }}
                      ></div>
                    )}
                    {realRoundIndex === 0 && matches.length === 8 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '280px',
                          top: `${(matchHeight + gapBetweenMatches) * 6 + matchHeight / 2 + 14}px`,
                          height: `${matchHeight + gapBetweenMatches}px`
                        }}
                      ></div>
                    )}
                    {/* Для сетки R32: вертикальные линии между парами матчей (1-2, 3-4, ... 15-16) */}
                    {realRoundIndex === 0 && matches.length === 16 &&
                      Array.from({ length: 8 }).map((_, pairIndex) => (
                        <div
                          key={`r32-connector-${pairIndex}`}
                          className="absolute w-0.5 bg-gray-300 z-0"
                          style={{
                            left: '280px',
                            top: `${(matchHeight + gapBetweenMatches) * (pairIndex * 2) + matchHeight / 2 + 14}px`,
                            height: `${matchHeight + gapBetweenMatches}px`
                          }}
                        ></div>
                      ))}
                    {/* Для сетки Single Elimination - 8: нижняя вертикальная линия (3-й и 4-й четвертьфиналы) */}
                    {realRoundIndex === 0 && matches.length === 4 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '280px',
                          top: `${(matchHeight + gapBetweenMatches) * 2 + matchHeight / 2 + 14}px`,
                          height: `${matchHeight + gapBetweenMatches}px`
                        }}
                      ></div>
                    )}
                    {/* Для сетки Single Elimination - 8: вертикальная линия, соединяющая полуфиналистов */}
                    {realRoundIndex === 1 && matches.length === 2 && playoffStage.rounds[0]?.matches === 4 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '246px', // -44 + 290: правый край линий от полуфиналов
                          top: `${matchHeight + gapBetweenMatches / 2 + 19}px`, // Опущено на 10px по запросу
                          height: `${(matchHeight + gapBetweenMatches) * 2 + 2}px`
                        }}
                      ></div>
                    )}
                    {/* Для сетки Single Elimination - 16: вертикальная линия, соединяющая два полуфинала */}
                    {realRoundIndex === 2 && matches.length === 2 && playoffStage.rounds[0]?.matches === 8 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '250px', // Правый край исходящих линий полуфиналов (-42 + 282) + 10px вправо
                          top: `${matchHeight / 2 + 274}px`, // Опущено на 10px по запросу
                          height: `${(matchHeight + gapBetweenMatches) * 4 + 2}px` // До центра 2-го полуфинала
                        }}
                      ></div>
                    )}
                    {/* Для сетки Single Elimination - 16: вертикальные линии между четвертьфиналами (1-2 и 3-4) */}
                    {realRoundIndex === 1 && matches.length === 4 && playoffStage.rounds[0]?.matches === 8 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '252px', // -45 + 297: правый край линий от четвертьфиналов
                          top: `${matchHeight / 2 + 99}px`,
                          height: `${matchHeight + gapBetweenMatches + 172}px`
                        }}
                      ></div>
                    )}
                    {realRoundIndex === 1 && matches.length === 4 && playoffStage.rounds[0]?.matches === 8 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '252px',
                          top: `${(matchHeight + gapBetweenMatches) * 2 + matchHeight / 2 + 439}px`,
                          height: `${matchHeight + gapBetweenMatches + 172}px`
                        }}
                      ></div>
                    )}
                    {/* Для сетки R32: вертикальные линии между матчами Optimi (1-2, 3-4, 5-6, 7-8) */}
                    {realRoundIndex === 1 && matches.length === 8 && playoffStage.rounds[0]?.matches === 16 &&
                      Array.from({ length: 4 }).map((_, pairIndex) => (
                        <div
                          key={`r32-optimi-connector-${pairIndex}`}
                          className="absolute w-0.5 bg-gray-300 z-0"
                          style={{
                            left: '252px',
                            top: `${(matchHeight + gapBetweenMatches) * (pairIndex * 4) + 139}px`,
                            height: `${(matchHeight + gapBetweenMatches) * 2 + 2}px`
                          }}
                        ></div>
                      ))}
                    {/* Для сетки R32: вертикальные линии между матчами Sferturi (1-2 и 3-4) */}
                    {realRoundIndex === 2 && matches.length === 4 && playoffStage.rounds[0]?.matches === 16 &&
                      Array.from({ length: 2 }).map((_, pairIndex) => (
                        <div
                          key={`r32-sferturi-connector-${pairIndex}`}
                          className="absolute w-0.5 bg-gray-300 z-0"
                          style={{
                            left: '254px',
                            top: `${(matchHeight + gapBetweenMatches) * (pairIndex * 4) + matchHeight / 2 + (pairIndex === 0 ? 264 : 944)}px`,
                            height: `${(matchHeight + gapBetweenMatches) * 2 + 342}px`
                          }}
                        ></div>
                      ))}
                    {/* Для сетки R32: вертикальная линия между двумя матчами Semifinale */}
                    {realRoundIndex === 3 && matches.length === 2 && playoffStage.rounds[0]?.matches === 16 && (
                      <div
                        className="absolute w-0.5 bg-gray-300 z-0"
                        style={{
                          left: '251px',
                          top: `${matchHeight / 2 + 609}px`,
                          height: `${(matchHeight + gapBetweenMatches) + 1197}px`
                        }}
                      ></div>
                    )}
                    {/* Для сетки Single Elimination - 8: горизонтальная линия от вертикали полуфиналов к финалу */}
                    {realRoundIndex === 1 && matches.length === 2 && playoffStage.rounds[0]?.matches === 4 && (
                      <div
                        className="absolute h-0.5 bg-gray-300 z-0"
                        style={{
                          left: '246px',
                          top: `${matchHeight + gapBetweenMatches / 2 + 9 + ((matchHeight + gapBetweenMatches) * 2 + 2) / 2}px`,
                          width: '280px',
                          transform: 'translateY(-50%)'
                        }}
                      ></div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Контейнер с чемпионом (победитель финала) */}
            {(() => {
              // Находим финал (последний раунд)
              const finalRound = playoffStage.rounds[playoffStage.rounds.length - 1];
              if (!finalRound || finalRound.matches !== 1) return null; // Только если финал с 1 матчем
              
              // Находим матч финала
              const finalRoundIndex = playoffStage.rounds.length - 1;
              let firstMatchNumber = 1;
              for (let prevRoundIndex = 0; prevRoundIndex < finalRoundIndex; prevRoundIndex++) {
                firstMatchNumber += playoffStage.rounds[prevRoundIndex].matches;
              }
              const finalMatchCode = `Playoff-${firstMatchNumber}`;
              const finalMatch = playoffMatches.find((m: Match) => m.matchCode === finalMatchCode);
              
              // Определяем победителя финала
              let champion: PlayoffPlayer | null = null;
              if (finalMatch && finalMatch.winnerId && finalMatch.result) {
                if (selectedTournament?.format === 'DOUBLES') {
                  // Для парных: определяем команду-победителя
                  if (finalMatch.winnerId && finalMatch.winner2Id) {
                    // Ищем команду по имени из матча
                    if (finalMatch.player1 && finalMatch.player3 && finalMatch.player2 && finalMatch.player4) {
                      const team1PlayerIds = [finalMatch.player1Id, finalMatch.player3Id].filter(Boolean);
                      const team2PlayerIds = [finalMatch.player2Id, finalMatch.player4Id].filter(Boolean);
                      
                      const team1Won = team1PlayerIds.includes(finalMatch.winnerId) && 
                                       team1PlayerIds.includes(finalMatch.winner2Id);
                      const team2Won = team2PlayerIds.includes(finalMatch.winnerId) && 
                                       team2PlayerIds.includes(finalMatch.winner2Id);
                      
                      if (team1Won && finalMatch.player1 && finalMatch.player3) {
                        const team1Name = `${finalMatch.player1.name} / ${finalMatch.player3.name}`;
                        champion = playoffPlayers.find((p: PlayoffPlayer) => 
                          p.name === team1Name
                        ) || null;
                      } else if (team2Won && finalMatch.player2 && finalMatch.player4) {
                        const team2Name = `${finalMatch.player2.name} / ${finalMatch.player4.name}`;
                        champion = playoffPlayers.find((p: PlayoffPlayer) => 
                          p.name === team2Name
                        ) || null;
                      }
                    }
                  }
                } else {
                  // Для одиночных: winnerId - это playerId
                  champion = playoffPlayers.find((p: PlayoffPlayer) => 
                    p.id === finalMatch.winnerId
                  ) || null;
                }
              }
              
              // Вычисляем позицию контейнера (ровно по центру первого этапа плей-оффа)
              const matchHeight = 90;
              const gapBetweenMatches = 80;
              const firstRoundMatches = playoffStage.rounds[0].matches;
              const firstRoundHeight = firstRoundMatches * matchHeight + (firstRoundMatches - 1) * gapBetweenMatches;
              const isQuarterPlayoff = firstRoundMatches === 4;
              const isSe16Playoff = firstRoundMatches === 8;
              const roundColumns = playoffStage.rounds.length;
              const firstColumnWidth = 230;
              const otherColumnWidth = 200;
              const columnGap = 96;

              // Позиционируем "Campion" в ту же колонку, что и Finala.
              const totalBracketWidth = firstColumnWidth + (roundColumns - 1) * (otherColumnWidth + columnGap);
              const finalColumnStart = firstColumnWidth + (roundColumns - 2) * (otherColumnWidth + columnGap) + columnGap;
              const finalColumnOffsetFromCenter = finalColumnStart - totalBracketWidth / 2;
              
              // Вычисляем позицию финала (как в основном коде)
              let finalTopOffset = 0;
              if (finalRoundIndex > 0) {
                const prevRoundMatches = playoffStage.rounds[finalRoundIndex - 1].matches;
                const prevMatch1Index = 0 * 2; // matchIndex = 0 для финала
                const prevMatch2Index = 0 * 2 + 1;
                const prevMatch1Top = prevMatch1Index * (matchHeight + gapBetweenMatches);
                const prevMatch2Top = prevMatch2Index * (matchHeight + gapBetweenMatches);
                const prevMatch1Center = prevMatch1Top + matchHeight / 2;
                const prevMatch2Center = prevMatch2Top + matchHeight / 2;
                const centerBetweenMatches = (prevMatch1Center + prevMatch2Center) / 2;
                finalTopOffset = centerBetweenMatches - matchHeight / 2;
              }
              
              // Центр контейнера финала
              const finalContainerCenter = finalTopOffset + matchHeight / 2;
              
              // Вычисляем высоту контейнера чемпионов (примерно)
              // Контейнер чемпионов имеет padding p-3 (12px сверху и снизу = 24px) + border-2 (2px сверху и снизу = 4px) + содержимое
              // Для парных: примерно 2 строки текста по 16px + mt-1 (4px) = примерно 36px содержимого + 28px padding/border = 64px
              // Для одиночных: примерно 1 строка текста по 16px = примерно 16px содержимого + 28px padding/border = 44px
              // Но лучше использовать ту же высоту, что и у контейнера финала (matchHeight), чтобы центры точно совпадали
              const championContainerHeight = matchHeight; // Используем ту же высоту, что и у финала
              
              // Позиционируем контейнер чемпионов так, чтобы его центр совпадал с центром контейнера финала
              // Используем центр контейнера финала для выравнивания, добавляем смещение вниз
              const championTopOffset = finalContainerCenter - championContainerHeight / 2 + 19;
              const championCardTopOffset =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 16
                  ? championTopOffset - 2820
                  : (applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8
                      ? championTopOffset - 25
                      : (isQuarterPlayoff ? championTopOffset - 100 : championTopOffset));
              const championCardLeftOffset =
                applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 16
                  ? '225px'
                  : (applyManualPlayoffTweaks && playoffStage.rounds[0]?.matches === 8 ? '-500px' : '0px');
              const championHeadingTopOffset =
                (applyManualPlayoffTweaks && (playoffStage.rounds[0]?.matches === 16 || playoffStage.rounds[0]?.matches === 8))
                  ? `${championCardTopOffset - 42}px`
                  : undefined;
              
              // Линия должна проходить через центр контейнера финала (между счетами)
              // Позиция линии относительно контейнера чемпионов, смещаем вниз, но на 5px выше чем контейнер
              const lineVerticalPosition = finalContainerCenter - championTopOffset + 15;

              if (!champion) {
                // Для SE-16 резервируем место под чемпионский блок всегда,
                // чтобы финальный матч не сдвигался после появления чемпиона.
                if (isSe16Playoff) {
                  return (
                    <div
                      key="champion-placeholder"
                      className="flex flex-col items-center relative"
                      style={{ order: 3, minHeight: `${playoffColumnsMinHeight}px`, width: '200px' }}
                    />
                  );
                }
                return null;
              }
              
              return (
                <div 
                  key="champion" 
                  className="flex flex-col items-center relative" 
                  style={
                    isQuarterPlayoff
                      ? {
                          position: 'absolute',
                          left: `calc(50% + ${finalColumnOffsetFromCenter}px)`,
                          top: '0px',
                          minHeight: `${playoffColumnsMinHeight}px`,
                          width: '200px'
                        }
                      : {
                          order: 3,
                          minHeight: `${playoffColumnsMinHeight}px`,
                          width: '200px',
                          transform: undefined
                        }
                  }
                >
                  <h3
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      textAlign: 'center',
                      color: '#374151',
                      transform: undefined,
                      position: (applyManualPlayoffTweaks && (playoffStage.rounds[0]?.matches === 16 || playoffStage.rounds[0]?.matches === 8)) ? 'absolute' : undefined,
                      top: championHeadingTopOffset,
                      left: championCardLeftOffset,
                      width: (applyManualPlayoffTweaks && (playoffStage.rounds[0]?.matches === 16 || playoffStage.rounds[0]?.matches === 8)) ? '200px' : undefined,
                      zIndex: (applyManualPlayoffTweaks && (playoffStage.rounds[0]?.matches === 16 || playoffStage.rounds[0]?.matches === 8)) ? 2 : undefined
                    }}
                  >
                    {selectedTournament?.format === 'DOUBLES' ? 'Campioni' : 'Campion'}
                  </h3>
                  <div className="relative flex-shrink-0" style={{ minHeight: `${playoffColumnsMinHeight}px`, width: '200px' }}>
                    <div 
                      className="absolute left-0 flex items-center"
                      style={{
                        top: `${championCardTopOffset}px`,
                        left: championCardLeftOffset,
                        width: '100%'
                      }}
                    >
                      <div className="relative flex items-center w-full">
                        <div
                          className="border-2 border-yellow-400 rounded-lg bg-yellow-50 shadow-lg relative z-10 flex-1"
                          style={{
                            padding: '12px',
                            minHeight: '90px',
                            width: '200px',
                            display: selectedTournament?.format === 'DOUBLES' ? undefined : 'flex',
                            alignItems: selectedTournament?.format === 'DOUBLES' ? undefined : 'center',
                            justifyContent: selectedTournament?.format === 'DOUBLES' ? undefined : 'center'
                          }}
                        >
                          <div
                            className="text-center"
                            style={
                              selectedTournament?.format === 'DOUBLES'
                                ? undefined
                                : { minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }
                            }
                          >
                            {selectedTournament?.format === 'DOUBLES' && champion.name.includes(' / ') ? (
                              <div className="font-bold text-gray-900">
                                <div className="text-center" style={{ fontSize: '16px' }}>{champion.name.split(' / ')[0]}</div>
                                <div className="text-center" style={{ fontSize: '16px', marginTop: '4px' }}>{champion.name.split(' / ')[1]}</div>
                              </div>
                            ) : (
                              <div className="font-bold text-gray-900" style={{ fontSize: '16px' }}>
                                {champion.name}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="py-6" data-export-capture-root="true">
      {!isExportMode && <h1 className="text-2xl font-bold mb-6">Турнирные таблицы</h1>}

      {!isExportMode && (
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Турнир
            </label>
            <select
              value={selectedTournament?.id || ''}
              onChange={(e) => {
                const tournament = tournaments.find(t => t.id === e.target.value);
                setSelectedTournament(tournament || null);
                // Очищаем logosLoadedRef при смене турнира
                if (tournament) {
                  logosLoadedRef.current.delete(tournament.id);
                }
                // Сбрасываем уровень только если не восстанавливаем данные
                if (!isRestoring) {
                setSelectedLevel('');
                }
                setStandingsByGroup([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Выберите турнир</option>
            {tournamentsGrouped.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.items.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
            ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Уровень
            </label>
            <select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                setStandingsByGroup([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={!selectedTournament}
            >
              <option value="">Выберите уровень</option>
              {levels.map(l => (
                <option key={l.level} value={l.level}>
                  {l.level} ({l.playerCount} игроков)
                </option>
              ))}
            </select>
          </div>

          <div>
            {!!cacheStatusInfo && (
              <div className="text-xs text-gray-600 mb-2 text-right">
                <p>
                  Последнее изменение результатов:{' '}
                  <span className="font-medium">{formatDateTime(cacheStatusInfo.resultsUpdatedAt)}</span>
                </p>
                <p>
                  Последнее обновление кэша:{' '}
                  <span className={`font-medium ${isCacheOlderThanResults(cacheStatusInfo) ? 'text-red-600' : ''}`}>
                    {formatDateTime(cacheStatusInfo.cacheUpdatedAt)}
                  </span>
                  {(() => {
                    const tags: string[] = [];
                    if (cacheStatusInfo.cacheStatus !== 'ready') {
                      tags.push(cacheStatusInfo.cacheStatus);
                    }
                    if (cacheStatusInfo.cacheIsSmall) {
                      tags.push('small');
                    }
                    return tags.length > 0 ? ` (${tags.join(', ')})` : '';
                  })()}
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleCacheRefresh}
                disabled={cacheRefreshLoading || loading || !selectedTournament || !selectedLevel}
                className="w-full bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                title="Пересобрать и обновить кэш изображения сетки"
              >
                <svg
                  className={`w-5 h-5 ${cacheRefreshLoading ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Кэш
              </button>

              <button
                onClick={handleRefresh}
                disabled={loading || cacheRefreshLoading}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <svg
                  className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Обновить
              </button>
            </div>
          </div>
        </div>
        {!!cacheRefreshFeedback && (
          <p className="text-sm text-gray-600 mt-3">{cacheRefreshFeedback}</p>
        )}
      </div>
      )}

      {loading && standingsByGroup.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>Загрузка таблиц...</p>
        </div>
      )}

      {!loading &&
        selectedTournament &&
        selectedLevel &&
        standingsByGroup.length === 0 &&
        !(maxStage >= 2 && playoffStage && Array.isArray(playoffStage.rounds) && playoffStage.rounds.length > 0) && (
        <div className="text-center py-12 text-gray-500">
          <p>Нет данных для отображения</p>
          <p className="text-sm mt-2">Убедитесь, что:</p>
          <ul className="text-sm mt-2 text-left inline-block">
            <li>1. Жеребьёвка сохранена (игроки распределены по группам)</li>
            <li>2. Созданы группы и проведены матчи</li>
          </ul>
          <p className="text-sm mt-4 text-orange-600">
            💡 Если жеребьёвка уже сохранена, попробуйте пересохранить её на странице "Жеребьёвки"
          </p>
        </div>
      )}

      {!selectedTournament && !loading && tournaments.length > 0 && (
        <div className="text-center py-12 text-gray-500">
          <p>Выберите турнир и уровень для просмотра таблиц</p>
        </div>
      )}

      {/* Заголовок турнира - показываем перед плей-оффом, если есть данные */}
      {selectedTournament && selectedLevel && (standingsByGroup.length > 0 || maxStage >= 2) && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden px-2 py-6 mb-6 text-center">
          <h1 
            className="text-6xl mb-2 text-gray-900"
            style={{ 
              fontFamily: 'Arial Black, Arial, sans-serif',
              fontStyle: 'italic',
              fontWeight: '900'
            }}
          >
            TENNIS TOUR MOLDOVA
          </h1>
          <h2 
            className="text-4xl mb-2 text-gray-800 font-bold"
          >
            {selectedTournament.name}
          </h2>
          <h2 
            className="text-3xl mb-4 text-gray-800 font-bold"
          >
            NIVEL {selectedLevel}
          </h2>
        </div>
      )}

      {/* Условный порядок отображения: если есть матчи следующего этапа, сначала плей-офф, потом группы */}
      {hasNextStageMatches && renderPlayoffStage()}

      {/* Показываем групповой этап только если maxStage >= 1 */}
      {standingsByGroup.length > 0 && maxStage >= 1 && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden px-2 py-6">
          <h2 className="text-xl font-bold mb-6 text-gray-800">FAZA GRUPELOR</h2>
          <div className="space-y-6">
            {standingsByGroup.map(({ group, standings, matches, matchProposals, error }) => (
            <div key={group.id}>
              {error ? (
                <div className="p-6 text-red-600">
                  <p>Ошибка загрузки таблицы: {error}</p>
                </div>
              ) : standings.length === 0 ? (
                <div className="p-6 text-gray-500 text-center">
                  <p>Нет данных в группе</p>
                </div>
              ) : (
                <div className="w-full">
                    {(() => {
                      const isDoubles = selectedTournament?.format === 'DOUBLES';
                      // Размеры столбцов для разных форматов турнира
                      const groupColWidth = isDoubles ? 48 : 48;
                      const playersColWidth = 150; // Одинаковая для парных и одиночных
                      const playerHeaderWidth = 80; // Одинаковая для парных и одиночных
                      const statsWidth = 295; // Статистика одинаковая
                      const tableWidth = groupColWidth + playersColWidth + (playerHeaderWidth * standings.length) + statsWidth;
                      
                      return (
                        <div className="w-full">
                    <table 
                            className="border-collapse border-2 border-gray-600 text-sm w-full" 
                      style={{ 
                              tableLayout: 'auto',
                        borderSpacing: 0
                      }}
                    >
                      <thead>
                            <tr style={{ height: isDoubles ? '48px' : '32px' }}>
                              <th className="border-2 border-gray-600 px-3 bg-gray-700 text-white font-bold text-[10px]" style={{ minWidth: `${groupColWidth}px`, width: `${groupColWidth}px`, height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingBottom: '4px', verticalAlign: 'middle' }}>
                            {group.name}
                          </th>
                              <th className="border-2 border-gray-600 px-2 bg-gray-200 font-semibold text-[10px] text-center" style={{ minWidth: `${playersColWidth}px`, width: `${playersColWidth}px`, height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingBottom: '4px', verticalAlign: 'middle' }}>
                                Jucători
                          </th>
                          {standings.map((player) => (
                                <th key={player.player.id} className="border-2 border-gray-600 px-2 bg-gray-200 font-semibold text-center text-[9px] leading-tight" style={{ minWidth: `${playerHeaderWidth}px`, width: `${playerHeaderWidth}px`, height: isDoubles ? '48px' : '32px', paddingTop: '2px', paddingBottom: '2px', verticalAlign: 'middle' }}>
                              {selectedTournament?.format === 'DOUBLES' && player.player.name.includes(' / ') ? (
                                // Для парных: разбиваем по " / " и отображаем каждое имя на новой строке
                                player.player.name.split(' / ').map((name, i) => (
                                  <div key={i} style={{ lineHeight: '1.1', marginBottom: i === 0 ? '1px' : '0', fontSize: '9px' }}>
                                    {name.split(' ').map((word, j) => (
                                      <div key={j} style={{ fontSize: '9px' }}>{word}</div>
                                    ))}
                                  </div>
                                ))
                              ) : (
                                // Для одиночных: разбиваем по пробелам
                                player.player.name.split(' ').map((word, i) => (
                                  <div key={i}>{word}</div>
                                ))
                              )}
                            </th>
                          ))}
                          {/* Заголовки колонок статистики */}
                          <th className="border-2 border-gray-600 bg-gray-200 font-semibold text-[10px] text-center" style={{ width: '25px', height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingRight: '2px', paddingBottom: '4px', paddingLeft: '2px', verticalAlign: 'middle', textAlign: 'center' }}>MP</th>
                          <th className="border-2 border-gray-600 bg-gray-200 font-semibold text-[10px] text-center" style={{ width: '25px', height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingRight: '2px', paddingBottom: '4px', paddingLeft: '2px', verticalAlign: 'middle', textAlign: 'center' }}>W</th>
                          <th className="border-2 border-gray-600 bg-gray-200 font-semibold text-[10px] text-center" style={{ width: '25px', height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingRight: '2px', paddingBottom: '4px', paddingLeft: '2px', verticalAlign: 'middle', textAlign: 'center' }}>L</th>
                          <th className="border-2 border-gray-600 bg-gray-200 font-semibold text-[10px] text-center" style={{ width: '55px', height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingRight: '2px', paddingBottom: '4px', paddingLeft: '2px', verticalAlign: 'middle', textAlign: 'center' }}>S</th>
                          <th className="border-2 border-gray-600 bg-gray-200 font-semibold text-[10px] text-center" style={{ width: '55px', height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingRight: '2px', paddingBottom: '4px', paddingLeft: '2px', verticalAlign: 'middle', textAlign: 'center' }}>S%</th>
                          <th className="border-2 border-gray-600 bg-gray-200 font-semibold text-[10px] text-center" style={{ width: '55px', height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingRight: '2px', paddingBottom: '4px', paddingLeft: '2px', verticalAlign: 'middle', textAlign: 'center' }}>G</th>
                          <th className="border-2 border-gray-600 bg-gray-200 font-semibold text-[10px] text-center" style={{ width: '55px', height: isDoubles ? '48px' : '32px', paddingTop: '4px', paddingRight: '2px', paddingBottom: '4px', paddingLeft: '2px', verticalAlign: 'middle', textAlign: 'center' }}>G%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standings.map((row, rowIdx) => {
                          // Количество команд, проходящих в плей-офф из каждой группы (берется из типа сетки)
                          // Приоритет: 1) qualifiedFromGroupsFromAPI (из ответа API), 2) playoffStage, 3) bracketTemplate
                          let qualifiedCount = qualifiedFromGroupsFromAPI || playoffStage?.qualifiedFromGroups || 0;
                          if (qualifiedCount === 0 && bracketTemplate) {
                            try {
                              const structure = typeof bracketTemplate.structure === 'string'
                                ? JSON.parse(bracketTemplate.structure)
                                : bracketTemplate.structure;
                              if (structure?.stages) {
                                const playoffStageData = structure.stages.find((s: any) => s.type === 'SINGLE_ELIMINATION');
                                qualifiedCount = playoffStageData?.qualifiedFromGroups || 0;
                              }
                            } catch (e) {
                              console.error('Ошибка парсинга структуры для подсветки:', e);
                            }
                          }
                          const shouldHighlight = qualifiedCount > 0 && rowIdx < qualifiedCount;
                          return (
                          <tr key={row.player.id} className={shouldHighlight ? 'bg-yellow-50' : ''} style={{ height: '40px' }}>
                            {(() => {
                              const isDoubles = selectedTournament?.format === 'DOUBLES';
                              const groupColWidth = isDoubles ? 48 : 48;
                              const playersColWidth = 150; // Одинаковая для парных и одиночных
                              const playerHeaderWidth = 75; // Одинаковая для парных и одиночных
                              
                              return (
                                <>
                                  <th className="border-2 border-gray-600 px-2 bg-gray-200 font-semibold text-xs text-center" style={{ width: `${groupColWidth}px`, height: '40px', paddingTop: '8px', paddingBottom: '8px', verticalAlign: 'middle' }}>
                              {rowIdx + 1}
                            </th>
                                  <td className="border-2 border-gray-600 bg-gray-50 text-xs font-medium text-left" style={{ width: `${playersColWidth}px`, height: '40px', paddingTop: '8px', paddingBottom: '8px', paddingLeft: '8px', paddingRight: '8px', verticalAlign: 'middle' }}>
                              {selectedTournament?.format === 'DOUBLES' && row.player.name.includes(' / ') ? (
                                <div>
                                  {row.player.name.split(' / ').map((name, idx) => (
                                    <div key={idx}>{name}</div>
                                  ))}
                                </div>
                              ) : (
                                row.player.name
                              )}
                            </td>
                            {standings.map((col, colIdx) => {
                              if (rowIdx === colIdx) {
                                return (
                                  <td key={col.player.id} className="border-2 border-gray-600 px-2 bg-gray-300 text-center" style={{ width: `${playerHeaderWidth}px`, height: '40px', paddingTop: '8px', paddingBottom: '8px', verticalAlign: 'middle' }}>
                                    <span className="text-gray-500">—</span>
                                  </td>
                                );
                              }
                              
                              // Находим матч между этими игроками/командами
                              const groupData = standingsByGroup.find(g => g.group.id === group.id);
                              const matches = groupData?.matches || [];
                              const groupMatchProposals = groupData?.matchProposals || [];
                              const isDoubles = selectedTournament?.format === 'DOUBLES';
                              
                              const match = matches.find((m: Match) => {
                                if (isDoubles) {
                                  // Для парных турниров: row.player.id и col.player.id - это teamId
                                  // Backend теперь возвращает team1Id и team2Id в матчах
                                  if (m.team1Id && m.team2Id) {
                                    // Проверяем, соответствует ли матч этим командам (в любом порядке)
                                    if ((m.team1Id === row.player.id && m.team2Id === col.player.id) ||
                                        (m.team1Id === col.player.id && m.team2Id === row.player.id)) {
                                      return true;
                                    }
                                  }
                                  return false;
                                } else {
                                  // Для одиночных турниров: обычная проверка
                                if (m.player1Id === row.player.id && m.player2Id === col.player.id) return true;
                                if (m.player1Id === col.player.id && m.player2Id === row.player.id) return true;
                                return false;
                                }
                              });
                              
                              // Определяем, какая команда/игрок в строке (team1 или team2)
                              let isRowPlayerTeam1 = false;
                              let isRowPlayerTeam2 = false;
                              if (isDoubles && match) {
                                // Для парных: используем team1Id и team2Id
                                isRowPlayerTeam1 = match.team1Id === row.player.id;
                                isRowPlayerTeam2 = match.team2Id === row.player.id;
                              } else if (match) {
                                // Для одиночных: используем player1Id и player2Id
                                isRowPlayerTeam1 = match.player1Id === row.player.id;
                                isRowPlayerTeam2 = match.player2Id === row.player.id;
                              }
                              
                              // Показываем матч, если он существует (независимо от результата)
                              // Матчи с результатом отображаются со счетом, без результата - как запланированные
                              if (!match) {
                                // Ищем match proposal для этой пары игроков
                                const proposal = groupMatchProposals.find((p: MatchProposal) => {
                                  const isPlayer1 = (p.player1Id === row.player.id && p.player2Id === col.player.id);
                                  const isPlayer2 = (p.player1Id === col.player.id && p.player2Id === row.player.id);
                                  return isPlayer1 || isPlayer2;
                                });
                                
                                // Логируем для отладки (только для первой группы и первых игроков)
                                if (group.name === '5-B' && (row.player.name.includes('Boldurat') || col.player.name.includes('Boldurat'))) {
                                  devLog(`[EXPORT] Поиск proposal для ${row.player.name} vs ${col.player.name}:`, {
                                    groupMatchProposalsCount: groupMatchProposals.length,
                                    foundProposal: !!proposal,
                                    proposalHasAvailability: proposal ? !!proposal.availability : false,
                                    proposalId: proposal?.id
                                  });
                                }
                                
                                // Если есть запланированный матч, показываем информацию о нем
                                if (proposal && proposal.availability) {
                                  const availability = proposal.availability;
                                  const clubName = availability.court?.club?.name || '';
                                  const courtName = availability.court?.name || '';
                                  const startTime = availability.publicProposalStartTime || availability.startTime || '';
                                  const endTime = availability.publicProposalEndTime || availability.endTime || '';
                                  
                                  // Форматируем дату в формате "Mi 05.11.2025" (день недели на румынском, 2 буквы)
                                  let date = '';
                                  if (availability.date) {
                                    const dateObj = new Date(availability.date);
                                    // Сокращения дней недели на румынском из 2 букв: Du, Lu, Ma, Mi, Jo, Vi, Sa
                                    const dayAbbreviations = ['Du', 'Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sa'];
                                    const dayOfWeek = dayAbbreviations[dateObj.getDay()];
                                    const day = String(dateObj.getDate()).padStart(2, '0');
                                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                    const year = dateObj.getFullYear();
                                    date = `${dayOfWeek} ${day}.${month}.${year}`;
                                  }
                                  
                                  return (
                                    <td key={col.player.id} className="border-2 border-gray-600 px-2 bg-blue-50 text-center" style={{ width: `${playerHeaderWidth}px`, height: '40px', paddingTop: '2px', paddingBottom: '2px', verticalAlign: 'top', fontSize: '8px', lineHeight: '1.1' }}>
                                      <div className="flex flex-col items-center justify-center" style={{ height: '100%' }}>
                                        <div className="font-medium">{clubName}</div>
                                        <div className="font-medium">cort {courtName}</div>
                                        <div>{date}</div>
                                        <div>{startTime}-{endTime}</div>
                                      </div>
                                    </td>
                                  );
                                }
                                
                                // Если нет запланированного матча, показываем "—"
                                return (
                                  <td key={col.player.id} className="border-2 border-gray-600 px-2 bg-white text-center" style={{ width: `${playerHeaderWidth}px`, height: '40px', paddingTop: '8px', paddingBottom: '8px', verticalAlign: 'middle' }}>
                                    <span className="text-gray-300">—</span>
                                  </td>
                                );
                              }
                              
                              // Если матч существует, но не имеет результата, показываем информацию о запланированном матче
                              if (!match.result) {
                                // Пытаемся получить информацию о резервировании корта из Match
                                const scheduledDate = match.scheduledDate ? new Date(match.scheduledDate) : null;
                                const scheduledTime = match.scheduledTime || '';
                                
                                // Если есть дата и время, показываем информацию о запланированном матче
                                if (scheduledDate && scheduledTime) {
                                  // Форматируем дату в формате "Mi 05.11.2025" (день недели на румынском, 2 буквы)
                                  // Сокращения дней недели на румынском из 2 букв: Du, Lu, Ma, Mi, Jo, Vi, Sa
                                  const dayAbbreviations = ['Du', 'Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sa'];
                                  const dayOfWeek = dayAbbreviations[scheduledDate.getDay()];
                                  const day = String(scheduledDate.getDate()).padStart(2, '0');
                                  const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
                                  const year = scheduledDate.getFullYear();
                                  const date = `${dayOfWeek} ${day}.${month}.${year}`;
                                  
                                  // Получаем информацию о корте и клубе из Match
                                  // Примечание: нужно добавить court в include при загрузке matches
                                  const courtName = match.court?.name || '';
                                  const clubName = match.court?.club?.name || '';
                                  
                                  // Если есть информация о корте, показываем ее
                                  if (courtName || clubName) {
                                    return (
                                      <td key={col.player.id} className="border-2 border-gray-600 px-2 bg-blue-50 text-center" style={{ width: `${playerHeaderWidth}px`, height: '40px', paddingTop: '2px', paddingBottom: '2px', verticalAlign: 'top', fontSize: '8px', lineHeight: '1.1' }}>
                                        <div className="flex flex-col items-center justify-center" style={{ height: '100%' }}>
                                          <div className="font-medium">{clubName}</div>
                                          <div className="font-medium">cort {courtName}</div>
                                          <div>{date}</div>
                                          <div>{scheduledTime}</div>
                                        </div>
                                      </td>
                                    );
                                  }
                                }
                                
                                // Если нет информации о корте, просто показываем "—"
                                return (
                                  <td key={col.player.id} className="border-2 border-gray-600 px-2 bg-white text-center" style={{ width: `${playerHeaderWidth}px`, height: '40px', paddingTop: '8px', paddingBottom: '8px', verticalAlign: 'middle' }}>
                                    <span className="text-gray-300">—</span>
                                  </td>
                                );
                              }
                              
                              // Определяем, кто победил
                              let isRowPlayerWinner = false;
                              if (isDoubles && match) {
                                // Для парных: проверяем, является ли команда в строке победителем
                                // Победитель определяется по team1Id/team2Id, если они есть
                                if (match.team1Id && match.team2Id) {
                                  // Используем winnerId и winner2Id для определения команды-победителя
                                  if (match.winnerId && match.winner2Id) {
                                    // Оба игрока команды должны быть в winnerId/winner2Id
                                    // Команда 1: team1Id соответствует player1Id и player3Id
                                    // Команда 2: team2Id соответствует player2Id и player4Id
                                    const team1Won = match.team1Id === row.player.id && 
                                                     [match.player1Id, match.player3Id].includes(match.winnerId) &&
                                                     [match.player1Id, match.player3Id].includes(match.winner2Id);
                                    const team2Won = match.team2Id === row.player.id && 
                                                     [match.player2Id, match.player4Id].includes(match.winnerId) &&
                                                     [match.player2Id, match.player4Id].includes(match.winner2Id);
                                    isRowPlayerWinner = team1Won || team2Won;
                                  } else if (match.winnerId) {
                                    // Fallback: если только один winnerId, проверяем по teamId
                                    // Для этого нужно найти, к какой команде относится winnerId
                                    const team1Info = match.team1Id === row.player.id;
                                    const team2Info = match.team2Id === row.player.id;
                                    if (team1Info && [match.player1Id, match.player3Id].includes(match.winnerId)) {
                                      isRowPlayerWinner = true;
                                    } else if (team2Info && [match.player2Id, match.player4Id].includes(match.winnerId)) {
                                      isRowPlayerWinner = true;
                                    }
                                  }
                                }
                              } else if (match) {
                                // Для одиночных: обычная проверка
                                isRowPlayerWinner = match.winnerId === row.player.id;
                              }
                              
                              // Получаем счет
                              let scoreData = formatMatchScoreForTable(match, row.player.id);
                              
                              // Если игрок/команда в строке - team2/player2, зеркалируем счет
                              if (isRowPlayerTeam2 && !isRowPlayerTeam1) {
                                const mirrored = mirrorScore(scoreData.setsScore, scoreData.gamesScore);
                                scoreData = {
                                  ...scoreData,
                                  setsScore: mirrored.setsScore,
                                  gamesScore: mirrored.gamesScore
                                };
                              }
                              
                              return (
                                <td key={col.player.id} className={`border-2 border-gray-600 text-center ${
                                  isRowPlayerWinner ? 'bg-green-100' : 'bg-red-50'
                                }`} style={{ width: `${playerHeaderWidth}px`, height: '40px', paddingTop: '4px', paddingRight: '2px', paddingBottom: '4px', paddingLeft: '2px', overflow: 'hidden', boxSizing: 'border-box', verticalAlign: 'middle' }}>
                                  <div className="flex flex-col items-center justify-center" style={{ height: '100%', lineHeight: '1.2', maxHeight: '24px' }}>
                                    <span className="text-[12px] font-medium">{scoreData.setsScore}</span>
                                    {scoreData.gamesScore && (
                                      <span className="text-[9px] text-gray-600 leading-tight mt-0.5">{scoreData.gamesScore}</span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                            {/* Ячейки статистики */}
                            <td className="border-2 border-gray-600 text-xs text-center" style={{ width: '25px', height: '40px', paddingTop: '2px', paddingRight: '2px', paddingBottom: '2px', paddingLeft: '2px', verticalAlign: 'middle' }}>
                              {row.matchesPlayed}
                            </td>
                            <td className="border-2 border-gray-600 text-xs text-center" style={{ width: '25px', height: '40px', paddingTop: '2px', paddingRight: '2px', paddingBottom: '2px', paddingLeft: '2px', verticalAlign: 'middle' }}>
                              {row.matchesWon}
                            </td>
                            <td className="border-2 border-gray-600 text-xs text-center" style={{ width: '25px', height: '40px', paddingTop: '2px', paddingRight: '2px', paddingBottom: '2px', paddingLeft: '2px', verticalAlign: 'middle' }}>
                              {row.matchesLost}
                            </td>
                            <td className="border-2 border-gray-600 text-xs text-center" style={{ width: '55px', height: '40px', paddingTop: '2px', paddingRight: '2px', paddingBottom: '2px', paddingLeft: '2px', verticalAlign: 'middle' }}>
                              {row.setsWon}:{row.setsLost}
                            </td>
                            <td className="border-2 border-gray-600 text-xs text-center" style={{ width: '55px', height: '40px', paddingTop: '2px', paddingRight: '2px', paddingBottom: '2px', paddingLeft: '2px', verticalAlign: 'middle' }}>
                              {row.setsWonPercent.toFixed(1)}%
                            </td>
                            <td className="border-2 border-gray-600 text-xs text-center" style={{ width: '55px', height: '40px', paddingTop: '2px', paddingRight: '2px', paddingBottom: '2px', paddingLeft: '2px', verticalAlign: 'middle' }}>
                              {row.gamesWon}:{row.gamesLost}
                            </td>
                            <td className="border-2 border-gray-600 text-xs text-center" style={{ width: '55px', height: '40px', paddingTop: '2px', paddingRight: '2px', paddingBottom: '2px', paddingLeft: '2px', verticalAlign: 'middle' }}>
                              {row.gamesWonPercent.toFixed(1)}%
                            </td>
                                </>
                              );
                            })()}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                        </div>
                      );
                    })()}
                </div>
              )}
            </div>
            ))}
          </div>
        </div>
      )}

      {/* Второй этап - Плей-офф */}
      {/* Показываем плей-офф только если maxStage >= 2 (для Mixed формата) */}
      {/* Если есть матчи следующего этапа, плей-офф уже показан выше, поэтому здесь скрываем */}
      {!hasNextStageMatches && renderPlayoffStage()}

      {/* Футер с логотипами и информацией об организаторе */}
      {(selectedTournament && selectedLevel && (standingsByGroup.length > 0 || maxStage >= 2)) && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden px-6 py-3 mt-3">
          <div className="flex items-center justify-between gap-8 flex-wrap">
            {/* Левый сегмент - Логотипы из карточки турнира */}
            {(() => {
              // Парсим logos из tournament
              let logos: string[] = [];
              if (selectedTournament && (selectedTournament as any).logos) {
                try {
                  logos = typeof (selectedTournament as any).logos === 'string' 
                    ? JSON.parse((selectedTournament as any).logos) 
                    : (selectedTournament as any).logos;
                } catch (e) {
                  console.error('Ошибка парсинга logos:', e);
                }
              }
              
              // Если logos не указаны, не отображаем футер с логотипами
              if (!logos || logos.length === 0) {
                return null;
              }
              
              return (
                <div className="flex items-center gap-6 flex-shrink-0">
                  {logos.map((logo, index) => (
                    (() => {
                      const normalizedLogo = typeof logo === 'string'
                        ? (
                            logo.startsWith('http://') ||
                            logo.startsWith('https://') ||
                            logo.startsWith('data:')
                              ? logo
                              : `/api/public-assets/${logo.replace(/^\/+/, '')}`
                          )
                        : '';
                      return (
                    <img 
                      key={index}
                      src={normalizedLogo} 
                      alt={`Logo ${index + 1}`} 
                      className="w-auto object-contain"
                      style={{ height: '160px' }}
                      onError={(e) => {
                        // Fallback если изображение не найдено
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                      );
                    })()
                  ))}
                </div>
              );
            })()}

            {/* Правый сегмент - Информация об организаторе */}
            <div className="flex-shrink-0 text-right">
              <div style={{ fontFamily: 'Arial Black, Arial, sans-serif', lineHeight: '1' }}>
                <div className="text-sm" style={{ fontWeight: '900', margin: 0, padding: 0 }}>
                  Organizator turneu:
                </div>
                <div className="text-base" style={{ fontWeight: '900', fontStyle: 'italic', margin: 0, padding: 0 }}>
                  TENNIS TOUR MOLDOVA
                </div>
                <div className="text-base" style={{ fontWeight: '900', margin: 0, padding: 0 }}>
                  VICTOR BOGDAN
                </div>
                <div className="text-sm" style={{ fontWeight: '900', margin: 0, padding: 0 }}>
                  +373 69602296
                </div>
                <div className="text-sm" style={{ fontWeight: '900', margin: 0, padding: 0 }}>
                  ttm.md
                </div>
                <div className="text-sm" style={{ fontWeight: '900', margin: 0, padding: 0 }}>
                  tennis.tour.moldova@gmail.com
                </div>
                <div className="text-sm" style={{ fontWeight: '900', margin: 0, padding: 0 }}>
                  Facebook.com/tennis.tour.moldova
                </div>
                <div className="text-sm" style={{ fontWeight: '900', margin: 0, padding: 0 }}>
                  Youtube.com/@tennis.tour.moldova
                </div>
                <div className="text-sm" style={{ fontWeight: '900', margin: 0, padding: 0 }}>
                  BuyMeACoffee.com/tennis.tour.moldova
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentStandings;
